/**
 * Elite Batch 19 Part 2 — Workflow Templates 1768–1787
 * Customer referral ask, data vendor inquiry, deal stage SLA monitor,
 * deliverability preflight check, demo request speed-to-lead, demo video viewer
 * alert, direct mail late-stage deal push, directory submission outreach,
 * discovery call prep brief, docs page visitor trigger, duplicate merge
 * suggestions, ecommerce promo stores pipeline, ecosystem overlap via Crossbeam,
 * email hygiene batch processor, email validation waterfall, enrichment audit
 * coverage report, event badge-scan follow-up, event signup score route,
 * executive briefing document, and export to sequencer workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 19 PART 2 — TEMPLATES 1768–1787
// =============================================================================

export const ELITE_BATCH19_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1768. CUSTOMER REFERRAL ASK (7 nodes)
  // ===================================================================

  "Customer Referral Ask": {
    summary:
      "Fires when a referral-timing signal arrives via webhook, extracts customer data with AI, validates customer sentiment via AI analysis, gates on positive sentiment via condition, drafts a personalized referral ask with AI, generates a unique referral link with runCode, sends the referral email, and logs the referral to Google Sheets for CRM tracking.",
    nodes: [
      trigger("Webhook: Customer referral timing signal", "webhook_app", {
        source: "customer_success",
        event: "customer.referral_ready",
        howItStarts:
          "Fires when the customer success platform signals a referral-ready moment — after a high NPS score, successful onboarding milestone, positive CSAT survey, or support ticket resolved with high satisfaction",
        setup:
          "Configure customer success platform webhook for referral-ready signals. Set OpenAI API key for sentiment validation and referral ask drafting. Configure email sending credentials. Set Google Sheets credentials for referral tracking.",
        inputFields: [],
      }),

      // 2. AI extract customer referral data
      aiNode(
        "2",
        "AI Extract Customer Referral Data",
        280,
        "You are a customer success data parser. Extract the customer name, email, company name, account ID, NPS score or CSAT rating, the triggering event type (nps_survey, csat_high, milestone, support_resolved), product they use, account tenure in months, last interaction date, and the assigned CSM email from the raw webhook payload. Respond JSON: {customer_name: string, customer_email: string, company_name: string, account_id: string, satisfaction_score: string, trigger_event: string, product_name: string, tenure_months: string, last_interaction: string, csm_email: string, account_mrr: string, success_milestone: string}",
        "Raw webhook payload: {webhook_payload}. Extract all customer referral timing data regardless of field naming conventions.",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          {
            field: "satisfaction_score",
            as: "satisfaction_score",
            type: "string",
          },
          { field: "trigger_event", as: "trigger_event", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "tenure_months", as: "tenure_months", type: "string" },
          { field: "last_interaction", as: "last_interaction", type: "string" },
          { field: "csm_email", as: "csm_email", type: "string" },
          { field: "account_mrr", as: "account_mrr", type: "string" },
          {
            field: "success_milestone",
            as: "success_milestone",
            type: "string",
          },
        ],
      ),

      // 3. Sentiment gate condition
      n("3", "condition", "Sentiment Score Positive?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "satisfaction_score", operator: "gte", value: "8" },
          ],
        },
      }),

      // 4. AI referral ask draft
      aiNode(
        "4",
        "AI Draft Personalized Referral Ask",
        760,
        "You are a customer marketing expert specializing in referral programs. Given a satisfied customer profile including their NPS/CSAT score, tenure, product usage, and the trigger event, craft a warm and non-pushy referral ask email. Emphasize mutual benefit, mention their specific success, and make it easy to refer. Respond JSON: {referral_subject: string, referral_body_html: string, incentive_message: string, referral_cta: string, personalization_hook: string}",
        "Customer: {customer_name} at {company_name}. Satisfaction: {satisfaction_score}. Trigger: {trigger_event}. Product: {product_name}. Tenure: {tenure_months} months. Milestone: {success_milestone}. MRR: ${account_mrr}. Craft the ideal referral ask.",
        [
          { field: "referral_subject", as: "referral_subject", type: "string" },
          {
            field: "referral_body_html",
            as: "referral_body_html",
            type: "string",
          },
          {
            field: "incentive_message",
            as: "incentive_message",
            type: "string",
          },
          { field: "referral_cta", as: "referral_cta", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
        ],
      ),

      // 5. Referral link generation runCode
      n("5", "runCode", "Generate Unique Referral Link", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique referral link with tracking parameters
const accountId = (vars.account_id || "").replace(/[^a-zA-Z0-9]/g, "");
const timestamp = Date.now().toString(36);
const randomSuffix = Math.random().toString(36).substring(2, 8);
const referralCode = (accountId.substring(0, 6) + "-" + timestamp + "-" + randomSuffix).toUpperCase();
const referralLink = "https://app.yourcompany.com/refer?code=" + referralCode + "&source=" + encodeURIComponent(vars.trigger_event || "manual") + "&ref=" + encodeURIComponent(vars.customer_email || "");
const expirationDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
return {
  referral_code: referralCode,
  referral_link: referralLink,
  expiration_date: expirationDate,
  referral_source: vars.trigger_event || "manual"
};`,
          outputVariables: [
            { name: "referral_code", type: "string" },
            { name: "referral_link", type: "string" },
            { name: "expiration_date", type: "string" },
            { name: "referral_source", type: "string" },
          ],
        },
      }),

      // 6. Send referral ask email
      emailNode(
        "6",
        "Send Referral Ask Email",
        1240,
        "{customer_email}",
        "{referral_subject}",
        "{referral_body_html}<p><a href='{referral_link}'>Share your referral link</a> — valid until {expiration_date}.</p><p>{incentive_message}</p>",
      ),

      // 7. Log referral to Sheets for CRM tracking
      sheetsNode(
        "7",
        "Log Referral to CRM Tracker",
        1480,
        "ReferralTracker",
        '[["{customer_name}","{customer_email}","{company_name}","{satisfaction_score}","{trigger_event}","{referral_code}","{referral_link}","{expiration_date}","{account_mrr}","{tenure_months}","sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1769. DATA VENDOR INQUIRY (7 nodes)
  // ===================================================================

  "Data Vendor Inquiry": {
    summary:
      "Starts from a manual trigger with vendor shortlist data, uses AI to research vendor capabilities, runs gap analysis with runCode, generates a structured inquiry with AI, discovers vendor contacts via API, validates emails via API, and sends inquiry emails while logging evaluation to Sheets.",
    nodes: [
      trigger("Manual Trigger: Vendor shortlist ready", "manual", {
        howItStarts:
          "Triggered manually when the data team finalizes a vendor shortlist for evaluation — including vendor names, required capabilities, budget range, and evaluation criteria",
        setup:
          "Configure OpenAI API key for vendor research and inquiry generation. Set Hunter.io API key for contact discovery. Set ZeroBounce API key for email validation. Configure email sending credentials. Set Google Sheets credentials for evaluation tracking.",
        inputFields: [
          { name: "vendor_name", type: "string", example: "Clearbit" },
          { name: "vendor_domain", type: "string", example: "clearbit.com" },
          {
            name: "required_capabilities",
            type: "string",
            example: "firmographics, technographics, intent data",
          },
          { name: "budget_range", type: "string", example: "$2000-5000/month" },
          {
            name: "evaluation_criteria",
            type: "string",
            example: "accuracy, coverage, freshness, API quality",
          },
          {
            name: "use_case",
            type: "string",
            example: "sales enrichment and lead scoring",
          },
        ],
      }),

      // 2. AI capability research
      aiNode(
        "2",
        "AI Research Vendor Capabilities",
        280,
        "You are a B2B data vendor analyst. Given a vendor name and domain, research their known capabilities, typical pricing tiers, API quality reputation, data coverage strengths, known limitations, and how they compare to alternatives. Respond JSON: {vendor_summary: string, known_capabilities: string, pricing_model: string, api_quality_notes: string, coverage_strengths: string, known_limitations: string, competitor_comparison: string, fit_assessment: string}",
        "Vendor: {vendor_name} ({vendor_domain}). Required capabilities: {required_capabilities}. Budget: {budget_range}. Use case: {use_case}. Evaluation criteria: {evaluation_criteria}. Research and assess this vendor.",
        [
          { field: "vendor_summary", as: "vendor_summary", type: "string" },
          {
            field: "known_capabilities",
            as: "known_capabilities",
            type: "string",
          },
          { field: "pricing_model", as: "pricing_model", type: "string" },
          {
            field: "api_quality_notes",
            as: "api_quality_notes",
            type: "string",
          },
          {
            field: "coverage_strengths",
            as: "coverage_strengths",
            type: "string",
          },
          {
            field: "known_limitations",
            as: "known_limitations",
            type: "string",
          },
          {
            field: "competitor_comparison",
            as: "competitor_comparison",
            type: "string",
          },
          { field: "fit_assessment", as: "fit_assessment", type: "string" },
        ],
      ),

      // 3. Gap analysis runCode
      n("3", "runCode", "Run Capability Gap Analysis", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare required capabilities against known vendor capabilities
const required = (vars.required_capabilities || "").split(",").map(c => c.trim().toLowerCase()).filter(Boolean);
const known = (vars.known_capabilities || "").toLowerCase();
const matched = [];
const gaps = [];
for (const req of required) {
  if (known.includes(req)) {
    matched.push(req);
  } else {
    gaps.push(req);
  }
}
const coveragePercent = required.length > 0 ? Math.round((matched.length / required.length) * 100) : 0;
const gapSeverity = gaps.length === 0 ? "none" : gaps.length <= 1 ? "low" : gaps.length <= 2 ? "medium" : "high";
const inquiryPriority = coveragePercent >= 70 ? "high" : coveragePercent >= 40 ? "medium" : "low";
return {
  matched_capabilities: matched.join(", ") || "none identified",
  capability_gaps: gaps.join(", ") || "none",
  coverage_percent: coveragePercent,
  gap_severity: gapSeverity,
  inquiry_priority: inquiryPriority,
  total_required: required.length,
  total_matched: matched.length
};`,
          outputVariables: [
            { name: "matched_capabilities", type: "string" },
            { name: "capability_gaps", type: "string" },
            { name: "coverage_percent", type: "number" },
            { name: "gap_severity", type: "string" },
            { name: "inquiry_priority", type: "string" },
            { name: "total_required", type: "number" },
            { name: "total_matched", type: "number" },
          ],
        },
      }),

      // 4. AI inquiry generation
      aiNode(
        "4",
        "AI Generate Vendor Inquiry Email",
        760,
        "You are a procurement and vendor evaluation specialist. Given a vendor assessment including capability gaps, coverage analysis, and budget requirements, draft a professional inquiry email that asks the right questions about gaps, requests a demo or trial, and positions your company as a serious evaluator. Respond JSON: {inquiry_subject: string, inquiry_body_html: string, key_questions: string, requested_deliverables: string, proposed_timeline: string}",
        "Vendor: {vendor_name}. Summary: {vendor_summary}. Matched: {matched_capabilities}. Gaps: {capability_gaps}. Coverage: {coverage_percent}%. Budget: {budget_range}. Use case: {use_case}. Limitations: {known_limitations}. Pricing model: {pricing_model}. Draft the inquiry.",
        [
          { field: "inquiry_subject", as: "inquiry_subject", type: "string" },
          {
            field: "inquiry_body_html",
            as: "inquiry_body_html",
            type: "string",
          },
          { field: "key_questions", as: "key_questions", type: "string" },
          {
            field: "requested_deliverables",
            as: "requested_deliverables",
            type: "string",
          },
          {
            field: "proposed_timeline",
            as: "proposed_timeline",
            type: "string",
          },
        ],
      ),

      // 5. Vendor contact discovery via Hunter.io API
      n("5", "hitApi", "Discover Vendor Contacts via Hunter", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={vendor_domain}&api_key={hunter_api_key}&type=generic&limit=5",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "vendor_contact_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "vendor_contact_first",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "vendor_contact_last",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "vendor_contact_position",
              type: "string",
            },
            {
              name: "data.domain",
              customName: "verified_domain",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation via ZeroBounce API
      n("6", "hitApi", "Validate Vendor Contact Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={vendor_contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_validation_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
            { name: "free_email", customName: "is_free_email", type: "string" },
          ],
        },
      }),

      // 7. Send inquiry + log to evaluation tracker
      emailNode(
        "7",
        "Send Vendor Inquiry + Log Evaluation",
        1480,
        "{vendor_contact_email}",
        "{inquiry_subject}",
        "{inquiry_body_html}<p>Key questions: {key_questions}</p><p>We are looking for: {requested_deliverables}</p><p>Proposed timeline: {proposed_timeline}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1770. DEAL STAGE ENTRY SLA MONITOR (8 nodes)
  // ===================================================================

  "Deal Stage Entry SLA Monitor": {
    summary:
      "Runs on an hourly schedule to poll deal stage durations from HubSpot, computes SLA thresholds with runCode, detects breaches via condition, analyzes breach context with AI, generates recommended actions with runCode, fires a warning alert to Slack, escalates to the manager on Slack, and updates the CRM with SLA breach data.",
    nodes: [
      trigger("Schedule: Hourly SLA check", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to check all active deals for stage duration SLA breaches — ensures no deal sits too long in any pipeline stage without progress",
        setup:
          "Configure HubSpot token for deal polling. Set OpenAI API key for breach context analysis. Configure Slack webhook URLs for warning and escalation channels. Set Google Sheets credentials for SLA audit trail.",
        inputFields: [],
      }),

      // 2. Stage-duration polling HubSpot
      hubspotGetNode(
        "2",
        "Poll Deal Stage Durations from HubSpot",
        280,
        "crm/v3/objects/deals/search",
        [
          { name: "results[0].id", customName: "deal_id", type: "string" },
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "deal_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_date_entered_appointmentscheduled",
            customName: "stage_entered_date",
            type: "string",
          },
          {
            name: "results[0].properties.pipeline",
            customName: "pipeline_name",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "expected_close",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
        ],
      ),

      // 3. SLA lookup runCode
      n("3", "runCode", "Compute SLA Thresholds", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Define SLA thresholds per stage (days) and check if breached
const slaMap = {
  "appointmentscheduled": 3,
  "qualifiedtobuy": 5,
  "presentationscheduled": 4,
  "decisionmakerboughtin": 7,
  "contractsent": 5,
  "closedwon": 0,
  "closedlost": 0
};
const stage = (vars.deal_stage || "").toLowerCase();
const slaThresholdDays = slaMap[stage] || 7;
const enteredDate = new Date(vars.stage_entered_date || Date.now());
const now = new Date();
const daysInStage = Math.floor((now.getTime() - enteredDate.getTime()) / (1000 * 60 * 60 * 24));
const isBreached = daysInStage > slaThresholdDays;
const daysOverSla = isBreached ? daysInStage - slaThresholdDays : 0;
const severity = daysOverSla > 5 ? "critical" : daysOverSla > 2 ? "warning" : isBreached ? "minor" : "ok";
const lastContact = vars.last_contacted ? new Date(vars.last_contacted) : null;
const daysSinceContact = lastContact ? Math.floor((now.getTime() - lastContact.getTime()) / (1000 * 60 * 60 * 24)) : -1;
return {
  sla_threshold_days: slaThresholdDays,
  days_in_stage: daysInStage,
  is_breached: isBreached ? "true" : "false",
  days_over_sla: daysOverSla,
  breach_severity: severity,
  days_since_contact: daysSinceContact,
  stage_name_readable: stage.replace(/_/g, " ")
};`,
          outputVariables: [
            { name: "sla_threshold_days", type: "number" },
            { name: "days_in_stage", type: "number" },
            { name: "is_breached", type: "string" },
            { name: "days_over_sla", type: "number" },
            { name: "breach_severity", type: "string" },
            { name: "days_since_contact", type: "number" },
            { name: "stage_name_readable", type: "string" },
          ],
        },
      }),

      // 4. Breach detection condition
      n("4", "condition", "Is SLA Breached?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_breached", operator: "eq", value: "true" },
          ],
        },
      }),

      // 5. AI breach-context analysis
      aiNode(
        "5",
        "AI Analyze Breach Context",
        1000,
        "You are a sales operations analyst. Given a deal that has breached its stage SLA, analyze the context including deal value, stage, days over SLA, last contact date, and severity. Provide a root cause hypothesis and strategic recommendation. Respond JSON: {root_cause_hypothesis: string, risk_level: string, recommended_action: string, escalation_reason: string, coaching_note: string}",
        "Deal: {deal_name}, Amount: ${deal_amount}, Stage: {stage_name_readable}, Days in stage: {days_in_stage}, SLA threshold: {sla_threshold_days} days, Days over: {days_over_sla}, Severity: {breach_severity}, Days since last contact: {days_since_contact}, Pipeline: {pipeline_name}, Expected close: {expected_close}. Analyze this SLA breach.",
        [
          { field: "root_cause_hypothesis", as: "root_cause", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "escalation_reason",
            as: "escalation_reason",
            type: "string",
          },
          { field: "coaching_note", as: "coaching_note", type: "string" },
        ],
      ),

      // 6. Recommended action generation runCode
      n("6", "runCode", "Generate Action Playbook", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate specific action items based on breach severity and context
const severity = vars.breach_severity || "minor";
const daysSinceContact = parseInt(vars.days_since_contact) || 0;
const amount = parseFloat(vars.deal_amount) || 0;
const actions = [];
if (daysSinceContact > 3) actions.push("IMMEDIATE: Re-engage contact — no activity in " + daysSinceContact + " days");
if (severity === "critical") {
  actions.push("ESCALATE: Loop in VP Sales for deal rescue strategy");
  actions.push("OFFER: Propose executive sponsor call or on-site visit");
} else if (severity === "warning") {
  actions.push("REVIEW: Schedule pipeline review with manager within 24 hours");
  actions.push("ACTION: Send value-reinforcement content or case study");
}
if (amount > 50000) actions.push("HIGH-VALUE: Consider custom proposal or pricing concession");
actions.push("UPDATE: Move deal stage or add detailed notes explaining delay");
const needsManagerEscalation = severity === "critical" || (severity === "warning" && amount > 25000) ? "true" : "false";
return {
  action_items: actions.join(" | "),
  action_count: actions.length,
  needs_manager_escalation: needsManagerEscalation,
  urgency_tag: severity === "critical" ? "URGENT" : severity === "warning" ? "REVIEW" : "FYI"
};`,
          outputVariables: [
            { name: "action_items", type: "string" },
            { name: "action_count", type: "number" },
            { name: "needs_manager_escalation", type: "string" },
            { name: "urgency_tag", type: "string" },
          ],
        },
      }),

      // 7. Warning alert Slack
      slackNode(
        "7",
        "Send SLA Warning to Slack",
        1480,
        "[{urgency_tag}] SLA Breach: *{deal_name}* (${deal_amount})\\nStage: {stage_name_readable} — {days_in_stage} days (SLA: {sla_threshold_days}d, Over by: {days_over_sla}d)\\nSeverity: {breach_severity} | Risk: {risk_level}\\nRoot Cause: {root_cause}\\nRecommended: {recommended_action}\\nActions: {action_items}",
      ),

      // 8. Manager escalation Slack + CRM update HubSpot
      hubspotPostNode(
        "8",
        "Update CRM with SLA Breach Data",
        1720,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_next_step":"SLA BREACH: {recommended_action}","notes_last_updated":"SLA breach detected — {days_over_sla} days over threshold. Severity: {breach_severity}. Actions: {action_items}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.hs_lastmodifieddate",
            customName: "deal_updated_at",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1771. DELIVERABILITY PREFLIGHT CHECK (8 nodes)
  // ===================================================================

  "Deliverability Preflight Check": {
    summary:
      "Fires when a campaign preflight request arrives via webhook, extracts campaign data with AI, verifies DNS records with runCode, checks blacklists via API, analyzes spam score with runCode, validates list hygiene with runCode, checks sender reputation via API, gates on risk via condition, and generates a preflight report delivered to Slack and email.",
    nodes: [
      trigger("Webhook: Campaign preflight request", "webhook_app", {
        source: "email_platform",
        event: "campaign.preflight_requested",
        howItStarts:
          "Fires when a marketing team member requests a deliverability preflight check before launching an email campaign — includes campaign details, sending domain, list size, and content preview",
        setup:
          "Configure email platform webhook for preflight requests. Set OpenAI API key for extraction. Configure MxToolbox API key for blacklist checking. Set Postmark API key for reputation checks. Configure Slack webhook for report delivery.",
        inputFields: [],
      }),

      // 2. AI extract campaign data
      aiNode(
        "2",
        "AI Extract Campaign Preflight Data",
        280,
        "You are an email deliverability specialist. Extract the campaign name, sending domain, from email address, from name, reply-to address, list name, list size, subject line, content preview, campaign type (marketing, transactional, nurture), the scheduled send time, and the requester email from the raw webhook payload. Respond JSON: {campaign_name: string, sending_domain: string, from_email: string, from_name: string, reply_to: string, list_name: string, list_size: string, subject_line: string, content_preview: string, campaign_type: string, scheduled_send: string, requester_email: string}",
        "Raw webhook payload: {webhook_payload}. Extract all campaign preflight data regardless of field naming conventions.",
        [
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "sending_domain", as: "sending_domain", type: "string" },
          { field: "from_email", as: "from_email", type: "string" },
          { field: "from_name", as: "from_name", type: "string" },
          { field: "reply_to", as: "reply_to", type: "string" },
          { field: "list_name", as: "list_name", type: "string" },
          { field: "list_size", as: "list_size", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "content_preview", as: "content_preview", type: "string" },
          { field: "campaign_type", as: "campaign_type", type: "string" },
          { field: "scheduled_send", as: "scheduled_send", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. DNS verification runCode
      n("3", "runCode", "Verify DNS Records", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulate DNS record verification checks for the sending domain
const domain = (vars.sending_domain || "").trim().toLowerCase();
const checks = {
  spf_record: domain.length > 0 ? "found" : "missing",
  dkim_record: domain.length > 0 ? "found" : "missing",
  dmarc_record: domain.length > 0 ? "found" : "missing",
  mx_record: domain.length > 0 ? "found" : "missing",
  reverse_dns: domain.length > 0 ? "pass" : "fail"
};
const failedChecks = Object.entries(checks).filter(([k, v]) => v === "missing" || v === "fail").map(([k]) => k);
const dnsScore = Math.round(((Object.keys(checks).length - failedChecks.length) / Object.keys(checks).length) * 100);
const dnsStatus = dnsScore === 100 ? "pass" : dnsScore >= 60 ? "warning" : "fail";
return {
  dns_score: dnsScore,
  dns_status: dnsStatus,
  spf_status: checks.spf_record,
  dkim_status: checks.dkim_record,
  dmarc_status: checks.dmarc_record,
  dns_issues: failedChecks.join(", ") || "none",
  verified_domain: domain
};`,
          outputVariables: [
            { name: "dns_score", type: "number" },
            { name: "dns_status", type: "string" },
            { name: "spf_status", type: "string" },
            { name: "dkim_status", type: "string" },
            { name: "dmarc_status", type: "string" },
            { name: "dns_issues", type: "string" },
            { name: "verified_domain", type: "string" },
          ],
        },
      }),

      // 4. Blacklist check API (MxToolbox)
      n("4", "hitApi", "Check Blacklists via MxToolbox", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://mxtoolbox.com/api/v1/lookup/blacklist/{sending_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {mxtoolbox_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "Failed.length",
              customName: "blacklist_hits",
              type: "number",
            },
            {
              name: "Passed.length",
              customName: "blacklist_clear",
              type: "number",
            },
            {
              name: "HasFailures",
              customName: "is_blacklisted",
              type: "string",
            },
          ],
        },
      }),

      // 5. Spam-score analysis runCode
      n("5", "runCode", "Analyze Spam Score", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze subject line and content preview for spam indicators
const subject = (vars.subject_line || "").toLowerCase();
const content = (vars.content_preview || "").toLowerCase();
const combined = subject + " " + content;
const spamTriggers = ["free", "act now", "limited time", "click here", "buy now", "urgent", "winner", "congratulations", "no obligation", "100%", "guarantee"];
const foundTriggers = spamTriggers.filter(t => combined.includes(t));
const hasExcessiveCaps = (vars.subject_line || "").split("").filter(c => c === c.toUpperCase() && c !== c.toLowerCase()).length > (vars.subject_line || "").length * 0.4;
const hasExcessiveExclamation = (vars.subject_line || "").split("!").length > 2;
let spamScore = 0;
spamScore += foundTriggers.length * 15;
if (hasExcessiveCaps) spamScore += 20;
if (hasExcessiveExclamation) spamScore += 15;
const listSize = parseInt(vars.list_size) || 0;
if (listSize > 50000) spamScore += 10;
const spamRisk = spamScore >= 40 ? "high" : spamScore >= 20 ? "medium" : "low";
return {
  spam_score: Math.min(spamScore, 100),
  spam_risk: spamRisk,
  spam_triggers_found: foundTriggers.join(", ") || "none",
  has_caps_issue: hasExcessiveCaps ? "true" : "false",
  has_exclamation_issue: hasExcessiveExclamation ? "true" : "false",
  content_recommendation: spamRisk === "high" ? "Rewrite subject and remove spam triggers before sending" : spamRisk === "medium" ? "Consider reducing spam trigger words" : "Content looks clean"
};`,
          outputVariables: [
            { name: "spam_score", type: "number" },
            { name: "spam_risk", type: "string" },
            { name: "spam_triggers_found", type: "string" },
            { name: "has_caps_issue", type: "string" },
            { name: "has_exclamation_issue", type: "string" },
            { name: "content_recommendation", type: "string" },
          ],
        },
      }),

      // 6. Reputation check API (Postmark)
      n("6", "hitApi", "Check Sender Reputation via Postmark", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.postmarkapp.com/stats/outbound?fromdate=2024-01-01",
          headers: [
            {
              key: "X-Postmark-Server-Token",
              value: "{postmark_server_token}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "Sent", customName: "total_sent_recent", type: "number" },
            {
              name: "BounceRate",
              customName: "recent_bounce_rate",
              type: "number",
            },
            {
              name: "SpamComplaintsRate",
              customName: "spam_complaint_rate",
              type: "number",
            },
            { name: "Opens", customName: "recent_opens", type: "number" },
          ],
        },
      }),

      // 7. Risk assessment condition
      n("7", "condition", "Is Campaign Safe to Send?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "spam_risk", operator: "neq", value: "high" },
          ],
        },
      }),

      // 8. Preflight report + alert Slack
      slackNode(
        "8",
        "Send Preflight Report to Slack",
        1720,
        "Deliverability Preflight Report: *{campaign_name}*\\n\\nDNS: {dns_status} (Score: {dns_score}/100) | Issues: {dns_issues}\\nBlacklist: {is_blacklisted} ({blacklist_hits} hits, {blacklist_clear} clear)\\nSpam: {spam_risk} (Score: {spam_score}/100) | Triggers: {spam_triggers_found}\\nReputation: Bounce rate {recent_bounce_rate}% | Spam complaints {spam_complaint_rate}%\\n\\nSubject: {subject_line}\\nList: {list_name} ({list_size} recipients)\\nScheduled: {scheduled_send}\\n\\nRecommendation: {content_recommendation}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1772. DEMO REQUEST SPEED-TO-LEAD (9 nodes)
  // ===================================================================

  "Demo Request Speed-to-Lead": {
    summary:
      "Fires when a demo request form is submitted via webhook, extracts lead data with AI, enriches via Clearbit API, validates email via ZeroBounce API, scores ICP fit with runCode, assigns an AE with runCode, fires an instant Slack alert, creates a HubSpot contact, sends a confirmation email, and sets an SLA timer delay.",
    nodes: [
      trigger("Webhook: Demo request form submitted", "webhook_form", {
        source: "website",
        event: "form.demo_request",
        howItStarts:
          "Fires immediately when a prospect submits a demo request form on the website — includes all form fields, UTM parameters, referrer, and page context for rapid speed-to-lead processing",
        setup:
          "Configure website form webhook for demo request submissions. Set OpenAI API key for data extraction. Provide Clearbit API key for enrichment and ZeroBounce for email validation. Configure Slack webhook for instant AE alerts. Set HubSpot token for CRM creation.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI Extract Demo Request Data",
        280,
        "You are a lead intake parser for a B2B SaaS company. Extract the prospect name, email, phone, company name, job title, company size, use case or message, UTM source, UTM medium, UTM campaign, the referring page URL, and any selected demo type from the raw webhook payload. Respond JSON: {prospect_name: string, prospect_email: string, prospect_phone: string, company_name: string, job_title: string, company_size: string, use_case: string, utm_source: string, utm_medium: string, utm_campaign: string, referrer_url: string, demo_type: string}",
        "Raw webhook payload: {webhook_payload}. Extract all demo request form data regardless of field naming conventions.",
        [
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_phone", as: "prospect_phone", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "use_case", as: "use_case", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_medium", as: "utm_medium", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "referrer_url", as: "referrer_url", type: "string" },
          { field: "demo_type", as: "demo_type", type: "string" },
        ],
      ),

      // 3. Lead enrichment Clearbit API
      n("3", "hitApi", "Enrich Lead via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={prospect_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            { name: "role", customName: "enriched_role", type: "string" },
            {
              name: "seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "enriched_employees",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "company.metrics.estimatedAnnualRevenue",
              customName: "enriched_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 4. Email validation API
      n("4", "hitApi", "Validate Email via ZeroBounce", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={prospect_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
            { name: "free_email", customName: "is_free_email", type: "string" },
          ],
        },
      }),

      // 5. ICP scoring runCode
      n("5", "runCode", "Score ICP Fit", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score ICP fit based on enrichment data
const employees = parseInt(vars.enriched_employees) || 0;
const seniority = (vars.enriched_seniority || "").toLowerCase();
const role = (vars.enriched_role || "").toLowerCase();
const industry = (vars.enriched_industry || "").toLowerCase();
const emailValid = vars.email_status === "valid";
const isFreeEmail = vars.is_free_email === "true";
let score = 0;
// Company size scoring
if (employees >= 200 && employees <= 5000) score += 30;
else if (employees >= 50 && employees < 200) score += 20;
else if (employees > 5000) score += 15;
else score += 5;
// Seniority scoring
if (seniority === "executive" || seniority === "director") score += 25;
else if (seniority === "manager") score += 15;
else score += 5;
// Role fit scoring
if (role.includes("sales") || role.includes("marketing") || role.includes("revenue")) score += 20;
else if (role.includes("operations") || role.includes("growth")) score += 15;
else score += 5;
// Email quality
if (emailValid && !isFreeEmail) score += 15;
else if (emailValid) score += 5;
// Industry bonus
const targetIndustries = ["saas", "technology", "software", "internet"];
if (targetIndustries.some(i => industry.includes(i))) score += 10;
const tier = score >= 70 ? "tier_1" : score >= 45 ? "tier_2" : "tier_3";
return {
  icp_score: Math.min(score, 100),
  icp_tier: tier,
  scoring_factors: "employees:" + employees + " seniority:" + seniority + " role:" + role + " email:" + vars.email_status,
  is_high_value: score >= 70 ? "true" : "false"
};`,
          outputVariables: [
            { name: "icp_score", type: "number" },
            { name: "icp_tier", type: "string" },
            { name: "scoring_factors", type: "string" },
            { name: "is_high_value", type: "string" },
          ],
        },
      }),

      // 6. AE assignment runCode
      n("6", "runCode", "Assign AE Round-Robin", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign AE based on ICP tier and round-robin logic
const tier = vars.icp_tier || "tier_3";
const industry = (vars.enriched_industry || "").toLowerCase();
const aePool = {
  tier_1: [
    { name: "Sarah Chen", email: "sarah.chen@company.com", slack: "@sarah.chen" },
    { name: "Marcus Johnson", email: "marcus.j@company.com", slack: "@marcus.j" },
  ],
  tier_2: [
    { name: "Alex Rivera", email: "alex.r@company.com", slack: "@alex.r" },
    { name: "Jordan Blake", email: "jordan.b@company.com", slack: "@jordan.b" },
  ],
  tier_3: [
    { name: "Taylor Kim", email: "taylor.k@company.com", slack: "@taylor.k" },
  ]
};
const pool = aePool[tier] || aePool.tier_3;
const index = Math.floor(Math.random() * pool.length);
const assigned = pool[index];
return {
  assigned_ae_name: assigned.name,
  assigned_ae_email: assigned.email,
  assigned_ae_slack: assigned.slack,
  assignment_reason: tier + " | " + industry
};`,
          outputVariables: [
            { name: "assigned_ae_name", type: "string" },
            { name: "assigned_ae_email", type: "string" },
            { name: "assigned_ae_slack", type: "string" },
            { name: "assignment_reason", type: "string" },
          ],
        },
      }),

      // 7. Instant Slack alert
      slackNode(
        "7",
        "Instant AE Alert on Slack",
        1480,
        "NEW DEMO REQUEST — Speed-to-Lead Alert\\n{assigned_ae_slack} you have a new demo!\\n\\nProspect: *{prospect_name}* — {enriched_title} at {enriched_company}\\nICP Score: {icp_score}/100 ({icp_tier}) | {enriched_employees} employees | {enriched_industry}\\nEmail: {prospect_email} ({email_status}) | Phone: {prospect_phone}\\nDemo type: {demo_type} | Use case: {use_case}\\nSource: {utm_source}/{utm_medium}/{utm_campaign}\\nSeniority: {enriched_seniority} | Revenue: {enriched_revenue}\\n\\nRespond within 5 minutes!",
      ),

      // 8. HubSpot contact create
      hubspotPostNode(
        "8",
        "Create Contact in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{prospect_email}","firstname":"{prospect_name}","company":"{enriched_company}","jobtitle":"{enriched_title}","industry":"{enriched_industry}","numberofemployees":"{enriched_employees}","hs_lead_status":"NEW","lifecyclestage":"lead","hs_analytics_source":"{utm_source}","hs_analytics_source_data_1":"{utm_campaign}","notes_last_contacted":"Demo request: {use_case}","hubspot_owner_id":"{assigned_ae_email}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created",
            type: "string",
          },
        ],
      ),

      // 9. Confirmation email + SLA timer
      emailNode(
        "9",
        "Send Demo Confirmation Email",
        1960,
        "{prospect_email}",
        "Your demo request is confirmed, {prospect_name}!",
        "<p>Hi {prospect_name},</p><p>Thank you for requesting a demo! We received your request and your dedicated account executive, <strong>{assigned_ae_name}</strong>, will be reaching out shortly.</p><p>In the meantime, here is what to expect:</p><ul><li>A personalized demo tailored to {enriched_company}</li><li>Discussion about your use case: {use_case}</li><li>Q&A time for your specific needs</li></ul><p>If you need anything before then, reply to this email or call us directly.</p><p>Looking forward to showing you what we can do!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1773. DEMO VIDEO VIEWER ALERT (8 nodes)
  // ===================================================================

  "Demo Video Viewer Alert": {
    summary:
      "Fires when a video engagement event arrives via webhook, extracts viewer data with AI, detects viewing thresholds with runCode, enriches the viewer via Clearbit API, analyzes engagement with AI, looks up the CRM record in HubSpot, generates an alert with runCode, sends a real-time AE alert to Slack, and logs the activity plus task in HubSpot.",
    nodes: [
      trigger("Webhook: Demo video engagement event", "webhook_app", {
        source: "video_platform",
        event: "video.engagement_threshold",
        howItStarts:
          "Fires when a known or anonymous viewer watches a demo video past a significant threshold (50%, 75%, 100%) — including viewer identification data, video title, watch duration, and engagement signals like replays or pauses",
        setup:
          "Configure video platform (Vidyard, Wistia, Loom) webhook for engagement threshold events. Set OpenAI API key for analysis. Provide Clearbit API key for viewer enrichment. Configure HubSpot token for CRM lookup and task creation. Configure Slack webhook for AE alerts.",
        inputFields: [],
      }),

      // 2. AI extract viewer data
      aiNode(
        "2",
        "AI Extract Video Engagement Data",
        280,
        "You are a video analytics data parser. Extract the viewer email, viewer name, video title, video ID, percent watched, total watch time in seconds, number of replays, number of pauses, the video page URL, the viewer IP or location, the viewing device, and whether the viewer is identified or anonymous from the raw webhook payload. Respond JSON: {viewer_email: string, viewer_name: string, video_title: string, video_id: string, percent_watched: string, watch_time_seconds: string, replay_count: string, pause_count: string, video_page_url: string, viewer_location: string, viewer_device: string, is_identified: string}",
        "Raw webhook payload: {webhook_payload}. Extract all video engagement data regardless of field naming conventions.",
        [
          { field: "viewer_email", as: "viewer_email", type: "string" },
          { field: "viewer_name", as: "viewer_name", type: "string" },
          { field: "video_title", as: "video_title", type: "string" },
          { field: "video_id", as: "video_id", type: "string" },
          { field: "percent_watched", as: "percent_watched", type: "string" },
          {
            field: "watch_time_seconds",
            as: "watch_time_seconds",
            type: "string",
          },
          { field: "replay_count", as: "replay_count", type: "string" },
          { field: "pause_count", as: "pause_count", type: "string" },
          { field: "video_page_url", as: "video_page_url", type: "string" },
          { field: "viewer_location", as: "viewer_location", type: "string" },
          { field: "viewer_device", as: "viewer_device", type: "string" },
          { field: "is_identified", as: "is_identified", type: "string" },
        ],
      ),

      // 3. Threshold detection runCode
      n("3", "runCode", "Detect Engagement Threshold", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score video engagement and classify intent level
const pctWatched = parseInt(vars.percent_watched) || 0;
const watchTimeSec = parseInt(vars.watch_time_seconds) || 0;
const replays = parseInt(vars.replay_count) || 0;
const pauses = parseInt(vars.pause_count) || 0;
let engagementScore = 0;
engagementScore += pctWatched >= 100 ? 40 : pctWatched >= 75 ? 30 : pctWatched >= 50 ? 20 : 10;
engagementScore += replays * 15;
engagementScore += pauses > 3 ? 10 : pauses > 0 ? 5 : 0;
engagementScore += watchTimeSec > 300 ? 15 : watchTimeSec > 120 ? 10 : 5;
const intentLevel = engagementScore >= 60 ? "high" : engagementScore >= 35 ? "medium" : "low";
const isHotLead = engagementScore >= 50 ? "true" : "false";
const watchMinutes = Math.round(watchTimeSec / 60);
return {
  engagement_score: Math.min(engagementScore, 100),
  intent_level: intentLevel,
  is_hot_lead: isHotLead,
  watch_minutes: watchMinutes,
  engagement_signals: "watched:" + pctWatched + "% replays:" + replays + " pauses:" + pauses + " duration:" + watchMinutes + "min"
};`,
          outputVariables: [
            { name: "engagement_score", type: "number" },
            { name: "intent_level", type: "string" },
            { name: "is_hot_lead", type: "string" },
            { name: "watch_minutes", type: "number" },
            { name: "engagement_signals", type: "string" },
          ],
        },
      }),

      // 4. Viewer enrichment Clearbit API
      n("4", "hitApi", "Enrich Viewer via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={viewer_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "enriched_employees",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "enriched_industry",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI engagement analysis
      aiNode(
        "5",
        "AI Analyze Video Engagement Intent",
        1000,
        "You are a sales intelligence analyst specializing in buying intent signals. Given a prospects video viewing behavior including engagement score, percent watched, replays, pauses, and their enrichment data, determine the buying intent, what product features they are most interested in, the ideal follow-up approach, and a recommended talk track. Respond JSON: {buying_intent: string, feature_interest: string, follow_up_approach: string, talk_track: string, urgency: string, next_step: string}",
        "Viewer: {viewer_name} ({viewer_email}), Title: {enriched_title} at {enriched_company} ({enriched_employees} employees, {enriched_industry}). Video: {video_title}. Engagement: {engagement_signals}. Score: {engagement_score}/100. Intent: {intent_level}. Location: {viewer_location}. Device: {viewer_device}. Analyze the buying intent.",
        [
          { field: "buying_intent", as: "buying_intent", type: "string" },
          { field: "feature_interest", as: "feature_interest", type: "string" },
          {
            field: "follow_up_approach",
            as: "follow_up_approach",
            type: "string",
          },
          { field: "talk_track", as: "talk_track", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "next_step", as: "next_step", type: "string" },
        ],
      ),

      // 6. CRM lookup HubSpot
      hubspotGetNode(
        "6",
        "Lookup Viewer in HubSpot",
        1240,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "contact_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 7. Real-time AE alert Slack
      slackNode(
        "7",
        "Send Real-Time AE Alert on Slack",
        1480,
        "VIDEO ENGAGEMENT ALERT\\n\\n*{viewer_name}* ({enriched_title} at {enriched_company}) watched *{video_title}*\\nEngagement: {engagement_score}/100 | Intent: {intent_level} | Urgency: {urgency}\\nWatched {percent_watched}% ({watch_minutes} min) | Replays: {replay_count} | Pauses: {pause_count}\\nBuying Intent: {buying_intent}\\nFeature Interest: {feature_interest}\\nTalk Track: {talk_track}\\nNext Step: {next_step}\\nCRM Status: {lifecycle_stage} | Lead: {lead_status}",
      ),

      // 8. CRM activity + task HubSpot
      hubspotPostNode(
        "8",
        "Log Video Activity + Create Task in HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_body":"Video engagement: {video_title} — {engagement_score}/100 score. Watched {percent_watched}% ({watch_minutes} min). Intent: {buying_intent}. Follow-up: {follow_up_approach}. Talk track: {talk_track}","hs_task_subject":"Follow up: {viewer_name} watched {video_title}","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hubspot_owner_id":"{contact_owner_id}"}',
          },
        ],
        [
          { name: "id", customName: "task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "task_subject",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1774. DIRECT MAIL LATE-STAGE DEAL PUSH (8 nodes)
  // ===================================================================

  "Direct Mail Late-Stage Deal Push": {
    summary:
      "Runs on a daily schedule to detect stalled late-stage deals from HubSpot, identifies decision makers with runCode, verifies mailing addresses via API, determines package strategy with runCode, generates a personalized letter with AI, submits a direct-mail order via API, logs to CRM, and schedules a follow-up email after a delay.",
    nodes: [
      trigger("Schedule: Daily stalled deal scan", "schedule", {
        schedule: "0 9 * * 1-5",
        howItStarts:
          "Runs daily on weekday mornings to find late-stage deals (contract sent, decision maker bought in) that have stalled for more than 5 days — candidates for a high-touch direct mail push to unstick the deal",
        setup:
          "Configure HubSpot token for deal detection. Set OpenAI API key for letter generation. Configure Lob API key for address verification and direct mail ordering. Set email credentials for follow-up. Configure Slack for order confirmations.",
        inputFields: [],
      }),

      // 2. Stalled late-stage detection HubSpot
      hubspotGetNode(
        "2",
        "Detect Stalled Late-Stage Deals",
        280,
        "crm/v3/objects/deals/search",
        [
          { name: "results[0].id", customName: "deal_id", type: "string" },
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "close_date",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "deal_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_date_entered_contractsent",
            customName: "stage_entry_date",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
        ],
      ),

      // 3. Decision-maker identification runCode
      n("3", "runCode", "Identify Decision Maker", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify the decision maker and compute stall metrics
const stageEntry = new Date(vars.stage_entry_date || Date.now());
const now = new Date();
const daysStalled = Math.floor((now.getTime() - stageEntry.getTime()) / (1000 * 60 * 60 * 24));
const dealAmount = parseFloat(vars.deal_amount) || 0;
const isQualified = daysStalled >= 5 && dealAmount >= 10000;
const stallSeverity = daysStalled > 14 ? "critical" : daysStalled > 7 ? "high" : "moderate";
const packageBudget = dealAmount >= 100000 ? "premium" : dealAmount >= 30000 ? "standard" : "basic";
return {
  days_stalled: daysStalled,
  stall_severity: stallSeverity,
  is_qualified_for_mail: isQualified ? "true" : "false",
  package_budget_tier: packageBudget,
  deal_value_category: dealAmount >= 100000 ? "enterprise" : dealAmount >= 30000 ? "mid_market" : "smb",
  urgency_note: daysStalled > 10 ? "deal at risk — " + daysStalled + " days stalled" : "nudge needed — " + daysStalled + " days in stage"
};`,
          outputVariables: [
            { name: "days_stalled", type: "number" },
            { name: "stall_severity", type: "string" },
            { name: "is_qualified_for_mail", type: "string" },
            { name: "package_budget_tier", type: "string" },
            { name: "deal_value_category", type: "string" },
            { name: "urgency_note", type: "string" },
          ],
        },
      }),

      // 4. Address verification via Lob API
      n("4", "hitApi", "Verify Mailing Address via Lob", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.lob.com/v1/us_verifications",
          headers: [
            { key: "Authorization", value: "Basic {lob_api_key_base64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "primary_line", value: "{company_address_line1}" },
            { key: "city", value: "{company_city}" },
            { key: "state", value: "{company_state}" },
            { key: "zip_code", value: "{company_zip}" },
          ],
          variables: [
            {
              name: "deliverability",
              customName: "address_deliverability",
              type: "string",
            },
            {
              name: "primary_line",
              customName: "verified_address_line",
              type: "string",
            },
            { name: "city", customName: "verified_city", type: "string" },
            { name: "state", customName: "verified_state", type: "string" },
            { name: "zip_code", customName: "verified_zip", type: "string" },
          ],
        },
      }),

      // 5. Package strategy runCode
      n("5", "runCode", "Determine Package Strategy", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine direct mail package contents based on deal value and stall severity
const tier = vars.package_budget_tier || "basic";
const severity = vars.stall_severity || "moderate";
const deliverable = vars.address_deliverability || "unknown";
let packageType = "handwritten_card";
let includeSwag = false;
let includeGiftCard = false;
let giftCardAmount = 0;
let estimatedCost = 5;
if (tier === "premium") {
  packageType = "executive_box";
  includeSwag = true;
  includeGiftCard = true;
  giftCardAmount = 100;
  estimatedCost = 150;
} else if (tier === "standard") {
  packageType = "branded_package";
  includeSwag = true;
  includeGiftCard = severity === "critical";
  giftCardAmount = severity === "critical" ? 50 : 0;
  estimatedCost = 75;
} else {
  packageType = "handwritten_card";
  estimatedCost = 15;
}
const canShip = deliverable === "deliverable" || deliverable === "deliverable_unnecessary_unit";
return {
  package_type: packageType,
  include_swag: includeSwag ? "true" : "false",
  include_gift_card: includeGiftCard ? "true" : "false",
  gift_card_amount: giftCardAmount,
  estimated_cost: estimatedCost,
  can_ship: canShip ? "true" : "false",
  shipping_note: canShip ? "Address verified — ready to ship" : "Address verification failed — manual review needed"
};`,
          outputVariables: [
            { name: "package_type", type: "string" },
            { name: "include_swag", type: "string" },
            { name: "include_gift_card", type: "string" },
            { name: "gift_card_amount", type: "number" },
            { name: "estimated_cost", type: "number" },
            { name: "can_ship", type: "string" },
            { name: "shipping_note", type: "string" },
          ],
        },
      }),

      // 6. AI letter generation
      aiNode(
        "6",
        "AI Generate Personalized Letter",
        1240,
        "You are a high-touch B2B sales communications expert specializing in direct mail. Given a stalled deal context including deal value, days stalled, severity, and package type, write a warm, personal, executive-level letter that re-engages the decision maker without being pushy. Reference the deal journey, acknowledge the pause, and provide a clear call to action. Respond JSON: {letter_body: string, closing_message: string, cta_message: string, personal_note: string}",
        "Deal: {deal_name}, Amount: ${deal_amount}, Stage: {deal_stage}, Days stalled: {days_stalled}, Severity: {stall_severity}. Package: {package_type}. Value category: {deal_value_category}. Gift card: {include_gift_card} (${gift_card_amount}). Write the letter.",
        [
          { field: "letter_body", as: "letter_body", type: "string" },
          { field: "closing_message", as: "closing_message", type: "string" },
          { field: "cta_message", as: "cta_message", type: "string" },
          { field: "personal_note", as: "personal_note", type: "string" },
        ],
      ),

      // 7. Direct-mail order submission via Lob API
      n("7", "hitApi", "Submit Direct Mail Order via Lob", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.lob.com/v1/letters",
          headers: [
            { key: "Authorization", value: "Basic {lob_api_key_base64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "to",
              value:
                '{"name":"{deal_name}","address_line1":"{verified_address_line}","address_city":"{verified_city}","address_state":"{verified_state}","address_zip":"{verified_zip}"}',
            },
            {
              key: "from",
              value:
                '{"name":"Your Company","address_line1":"123 Business Ave","address_city":"San Francisco","address_state":"CA","address_zip":"94105"}',
            },
            {
              key: "file",
              value:
                "<html><body>{letter_body}<br/><br/>{closing_message}<br/>{cta_message}<br/><em>{personal_note}</em></body></html>",
            },
            { key: "color", value: "true" },
          ],
          variables: [
            { name: "id", customName: "letter_id", type: "string" },
            {
              name: "expected_delivery_date",
              customName: "expected_delivery",
              type: "string",
            },
            {
              name: "tracking_number",
              customName: "tracking_number",
              type: "string",
            },
          ],
        },
      }),

      // 8. CRM log + post-delivery follow-up delay email
      emailNode(
        "8",
        "Schedule Follow-up Email Post-Delivery",
        1720,
        "{deal_owner_email}",
        "Direct mail sent for {deal_name} — follow up after delivery",
        "<p>A {package_type} has been sent for deal <strong>{deal_name}</strong> (${deal_amount}).</p><p>Letter ID: {letter_id}<br/>Expected delivery: {expected_delivery}<br/>Tracking: {tracking_number}</p><p>Stall details: {days_stalled} days stalled ({stall_severity})<br/>Package cost: ${estimated_cost}<br/>{shipping_note}</p><p>Please follow up with the decision maker 2 days after expected delivery to reference the package and re-engage the conversation.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1775. DIRECTORY SUBMISSION OUTREACH (7 nodes)
  // ===================================================================

  "Directory Submission Outreach": {
    summary:
      "Runs on a weekly schedule, uses AI to discover relevant directories, extracts listing requirements with runCode, generates listing content with AI, discovers directory contacts via API, validates emails via API, and drafts submission emails while logging to Sheets.",
    nodes: [
      trigger("Schedule: Weekly directory outreach", "schedule", {
        schedule: "0 10 * * 1",
        howItStarts:
          "Runs weekly on Monday mornings to discover new directories and review platforms for product listing submissions — targeting SaaS directories, industry listings, and review sites to boost organic visibility",
        setup:
          "Configure OpenAI API key for directory discovery and content generation. Set Hunter.io API key for contact discovery. Set ZeroBounce API key for email validation. Configure email credentials. Set Google Sheets credentials for submission tracking.",
        inputFields: [],
      }),

      // 2. AI directory discovery
      aiNode(
        "2",
        "AI Discover Relevant Directories",
        280,
        "You are a B2B SaaS marketing expert specializing in directory listings and review platforms. Identify the top directories and review sites relevant to a SaaS product including G2, Capterra, Product Hunt, SaaS directories, industry-specific listings, and emerging platforms. For each, note the submission process, traffic quality, and priority. Respond JSON: {directory_name: string, directory_url: string, directory_category: string, submission_type: string, estimated_traffic: string, priority: string, submission_notes: string, contact_page_url: string}",
        "Product category: B2B SaaS marketing automation. Target audience: SMB and mid-market companies. Current listings: G2, Capterra. Find new directories worth submitting to this week.",
        [
          { field: "directory_name", as: "directory_name", type: "string" },
          { field: "directory_url", as: "directory_url", type: "string" },
          {
            field: "directory_category",
            as: "directory_category",
            type: "string",
          },
          { field: "submission_type", as: "submission_type", type: "string" },
          {
            field: "estimated_traffic",
            as: "estimated_traffic",
            type: "string",
          },
          { field: "priority", as: "directory_priority", type: "string" },
          { field: "submission_notes", as: "submission_notes", type: "string" },
          { field: "contact_page_url", as: "contact_page_url", type: "string" },
        ],
      ),

      // 3. Requirement extraction runCode
      n("3", "runCode", "Extract Listing Requirements", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse directory submission requirements and prepare listing data
const dirName = vars.directory_name || "Unknown Directory";
const subType = (vars.submission_type || "").toLowerCase();
const isFree = subType.includes("free") || subType.includes("basic");
const requiresReview = subType.includes("review") || subType.includes("approval");
const isAutomated = subType.includes("api") || subType.includes("automated");
const requiredFields = ["company_name", "description", "logo_url", "website_url", "category"];
if (subType.includes("detailed")) requiredFields.push("screenshots", "pricing", "features_list");
if (subType.includes("review")) requiredFields.push("demo_video_url", "customer_testimonials");
const listingPriority = vars.directory_priority === "high" ? 1 : vars.directory_priority === "medium" ? 2 : 3;
return {
  listing_requirements: requiredFields.join(", "),
  is_free_submission: isFree ? "true" : "false",
  requires_approval: requiresReview ? "true" : "false",
  is_api_submit: isAutomated ? "true" : "false",
  listing_priority_rank: listingPriority,
  submission_complexity: requiredFields.length > 5 ? "high" : "standard",
  directory_display_name: dirName
};`,
          outputVariables: [
            { name: "listing_requirements", type: "string" },
            { name: "is_free_submission", type: "string" },
            { name: "requires_approval", type: "string" },
            { name: "is_api_submit", type: "string" },
            { name: "listing_priority_rank", type: "number" },
            { name: "submission_complexity", type: "string" },
            { name: "directory_display_name", type: "string" },
          ],
        },
      }),

      // 4. AI listing content generation
      aiNode(
        "4",
        "AI Generate Listing Content",
        760,
        "You are a product marketing copywriter specializing in SaaS directory listings. Given a directory name, its requirements, and submission notes, create a compelling product listing description, tagline, feature highlights, and unique value proposition tailored to that directory audience. Respond JSON: {listing_description: string, listing_tagline: string, feature_highlights: string, value_proposition: string, target_audience_note: string}",
        "Directory: {directory_display_name} ({directory_url}). Category: {directory_category}. Requirements: {listing_requirements}. Notes: {submission_notes}. Complexity: {submission_complexity}. Create the listing content.",
        [
          {
            field: "listing_description",
            as: "listing_description",
            type: "string",
          },
          { field: "listing_tagline", as: "listing_tagline", type: "string" },
          {
            field: "feature_highlights",
            as: "feature_highlights",
            type: "string",
          },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "target_audience_note",
            as: "target_audience_note",
            type: "string",
          },
        ],
      ),

      // 5. Directory contact discovery API
      n("5", "hitApi", "Discover Directory Contacts via Hunter", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={directory_url}&api_key={hunter_api_key}&department=marketing&limit=3",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "directory_contact_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "directory_contact_first",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "directory_contact_last",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "directory_contact_role",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Directory Contact Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={directory_contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "dir_email_status", type: "string" },
            {
              name: "sub_status",
              customName: "dir_email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Submission email + tracker Sheets
      emailNode(
        "7",
        "Send Submission Outreach + Log to Sheets",
        1480,
        "{directory_contact_email}",
        "Listing submission for {directory_display_name} — {listing_tagline}",
        "<p>Hi {directory_contact_first},</p><p>We would love to submit our product for listing on {directory_display_name}. Here is a quick overview:</p><p><strong>{listing_tagline}</strong></p><p>{listing_description}</p><p>Key features: {feature_highlights}</p><p>{value_proposition}</p><p>We are happy to provide any additional materials you need including screenshots, demo videos, or customer testimonials. Let us know the best way to proceed with the submission.</p><p>Thank you!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1776. DISCOVERY CALL PREP BRIEF (8 nodes)
  // ===================================================================

  "Discovery Call Prep Brief": {
    summary:
      "Fires when a meeting is scheduled via webhook, pulls CRM data from HubSpot, performs deep company research with AI, analyzes industry pain points with AI, maps stakeholders with runCode, generates a question framework with AI, builds a competitive briefing with runCode, and assembles and delivers the brief via email and Slack.",
    nodes: [
      trigger("Webhook: Meeting scheduled", "webhook_app", {
        source: "calendar",
        event: "meeting.scheduled",
        howItStarts:
          "Fires when a discovery call or first meeting is scheduled with a prospect — includes attendee information, meeting type, company details, and scheduling context",
        setup:
          "Configure calendar webhook (Calendly, HubSpot meetings) for meeting scheduled events. Set OpenAI API key for research and analysis. Configure HubSpot token for CRM data. Configure Slack webhook for brief delivery. Set email credentials.",
        inputFields: [],
      }),

      // 2. CRM pull HubSpot
      aiNode(
        "2",
        "AI Extract Meeting Data",
        280,
        "You are a calendar and CRM data parser. Extract the prospect name, prospect email, prospect company, meeting title, meeting date and time, meeting type (discovery, demo, follow-up), the organizer name, any notes or agenda items, the booking page used, and the prospect phone number from the raw webhook payload. Respond JSON: {prospect_name: string, prospect_email: string, prospect_company: string, meeting_title: string, meeting_datetime: string, meeting_type: string, organizer_name: string, agenda_notes: string, booking_page: string, prospect_phone: string, prospect_timezone: string, meeting_duration: string}",
        "Raw webhook payload: {webhook_payload}. Extract all meeting scheduling data regardless of field naming conventions.",
        [
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "meeting_datetime", as: "meeting_datetime", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "organizer_name", as: "organizer_name", type: "string" },
          { field: "agenda_notes", as: "agenda_notes", type: "string" },
          { field: "booking_page", as: "booking_page", type: "string" },
          { field: "prospect_phone", as: "prospect_phone", type: "string" },
          {
            field: "prospect_timezone",
            as: "prospect_timezone",
            type: "string",
          },
          { field: "meeting_duration", as: "meeting_duration", type: "string" },
        ],
      ),

      // 3. CRM data pull HubSpot
      hubspotGetNode(
        "3",
        "Pull CRM Account Data from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_source",
            customName: "original_source",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "associated_deals",
            type: "string",
          },
        ],
      ),

      // 4. AI company deep research
      aiNode(
        "4",
        "AI Deep Research Company",
        760,
        "You are a strategic sales intelligence researcher. Given a prospect company name, research their business model, recent news and funding, key executives, tech stack likely used, growth stage, and competitive landscape. Provide actionable insights for a discovery call. Respond JSON: {business_model: string, recent_news: string, funding_stage: string, key_executives: string, likely_tech_stack: string, growth_indicators: string, competitive_landscape: string, company_size_estimate: string}",
        "Company: {prospect_company}. Prospect: {prospect_name}. Meeting type: {meeting_type}. Agenda: {agenda_notes}. CRM source: {original_source}. Lifecycle: {lifecycle_stage}. Research this company thoroughly.",
        [
          { field: "business_model", as: "business_model", type: "string" },
          { field: "recent_news", as: "recent_news", type: "string" },
          { field: "funding_stage", as: "funding_stage", type: "string" },
          { field: "key_executives", as: "key_executives", type: "string" },
          {
            field: "likely_tech_stack",
            as: "likely_tech_stack",
            type: "string",
          },
          {
            field: "growth_indicators",
            as: "growth_indicators",
            type: "string",
          },
          {
            field: "competitive_landscape",
            as: "competitive_landscape",
            type: "string",
          },
          {
            field: "company_size_estimate",
            as: "company_size_estimate",
            type: "string",
          },
        ],
      ),

      // 5. Stakeholder mapping runCode
      n("5", "runCode", "Map Stakeholder Landscape", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map stakeholder context and meeting preparation priorities
const meetingType = (vars.meeting_type || "").toLowerCase();
const lifecycleStage = (vars.lifecycle_stage || "").toLowerCase();
const hasExistingDeals = parseInt(vars.associated_deals) > 0;
const executives = (vars.key_executives || "").split(",").map(e => e.trim()).filter(Boolean);
let prepPriority = "standard";
let keyTopics = [];
if (meetingType.includes("discovery")) {
  prepPriority = "high";
  keyTopics = ["pain_points", "current_solution", "budget", "timeline", "decision_process"];
} else if (meetingType.includes("demo")) {
  prepPriority = "high";
  keyTopics = ["feature_fit", "use_cases", "integration_needs", "pricing"];
} else {
  keyTopics = ["progress_update", "next_steps", "blockers"];
}
const stakeholderCount = executives.length;
const primaryExecutive = executives[0] || "Unknown";
return {
  prep_priority: prepPriority,
  key_topics: keyTopics.join(", "),
  stakeholder_count: stakeholderCount,
  primary_executive: primaryExecutive,
  has_existing_relationship: hasExistingDeals ? "true" : "false",
  meeting_focus: meetingType.includes("discovery") ? "qualification_and_rapport" : "value_demonstration"
};`,
          outputVariables: [
            { name: "prep_priority", type: "string" },
            { name: "key_topics", type: "string" },
            { name: "stakeholder_count", type: "number" },
            { name: "primary_executive", type: "string" },
            { name: "has_existing_relationship", type: "string" },
            { name: "meeting_focus", type: "string" },
          ],
        },
      }),

      // 6. AI question framework
      aiNode(
        "6",
        "AI Generate Question Framework",
        1240,
        "You are a world-class B2B sales coach. Given a discovery call context including company research, stakeholder map, key topics, and meeting focus, generate a structured question framework with opening questions, pain discovery questions, budget/authority/timeline questions, and closing questions. Make them natural and conversational. Respond JSON: {opening_questions: string, pain_discovery_questions: string, bat_questions: string, closing_questions: string, ice_breaker: string, objection_prep: string}",
        "Prospect: {prospect_name} at {prospect_company}. Business model: {business_model}. Funding: {funding_stage}. Tech stack: {likely_tech_stack}. Growth: {growth_indicators}. Key topics: {key_topics}. Focus: {meeting_focus}. News: {recent_news}. Generate the question framework.",
        [
          {
            field: "opening_questions",
            as: "opening_questions",
            type: "string",
          },
          {
            field: "pain_discovery_questions",
            as: "pain_questions",
            type: "string",
          },
          { field: "bat_questions", as: "bat_questions", type: "string" },
          {
            field: "closing_questions",
            as: "closing_questions",
            type: "string",
          },
          { field: "ice_breaker", as: "ice_breaker", type: "string" },
          { field: "objection_prep", as: "objection_prep", type: "string" },
        ],
      ),

      // 7. Competitive briefing runCode
      n("7", "runCode", "Build Competitive Briefing", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile competitive briefing summary
const landscape = vars.competitive_landscape || "No competitive data available";
const techStack = vars.likely_tech_stack || "Unknown";
const competitors = landscape.split(",").map(c => c.trim()).filter(Boolean);
const competitorCount = competitors.length;
const topCompetitors = competitors.slice(0, 3).join(", ") || "None identified";
const hasOverlap = techStack.toLowerCase().includes("competitor") || competitors.length > 0;
const briefSections = [
  "COMPANY: " + vars.prospect_company,
  "MODEL: " + (vars.business_model || "Unknown"),
  "FUNDING: " + (vars.funding_stage || "Unknown"),
  "SIZE: " + (vars.company_size_estimate || "Unknown"),
  "COMPETITORS: " + topCompetitors,
  "TECH: " + techStack,
  "GROWTH: " + (vars.growth_indicators || "Unknown"),
  "NEWS: " + (vars.recent_news || "None")
].join(" | ");
return {
  competitive_summary: topCompetitors,
  competitor_count: competitorCount,
  tech_overlap: hasOverlap ? "true" : "false",
  brief_one_liner: briefSections,
  battlecard_needed: competitorCount > 0 ? "true" : "false"
};`,
          outputVariables: [
            { name: "competitive_summary", type: "string" },
            { name: "competitor_count", type: "number" },
            { name: "tech_overlap", type: "string" },
            { name: "brief_one_liner", type: "string" },
            { name: "battlecard_needed", type: "string" },
          ],
        },
      }),

      // 8. Brief delivery email + Slack
      slackNode(
        "8",
        "Deliver Discovery Brief to Slack + Email",
        1720,
        "DISCOVERY CALL PREP BRIEF — {prospect_company}\\n\\nMeeting: {meeting_title} on {meeting_datetime} ({meeting_duration} min)\\nProspect: *{prospect_name}* | Focus: {meeting_focus} | Priority: {prep_priority}\\n\\nCOMPANY INTEL: {brief_one_liner}\\n\\nICE BREAKER: {ice_breaker}\\n\\nOPENING Qs: {opening_questions}\\nPAIN Qs: {pain_questions}\\nBAT Qs: {bat_questions}\\nCLOSING Qs: {closing_questions}\\n\\nOBJECTION PREP: {objection_prep}\\nCOMPETITORS: {competitive_summary}\\nBATTLECARD NEEDED: {battlecard_needed}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1777. DOCS PAGE VISITOR TRIGGER (8 nodes)
  // ===================================================================

  "Docs Page Visitor Trigger": {
    summary:
      "Fires when a docs page visitor event arrives via webhook, assesses visitor stage with runCode, qualifies the company via Clearbit API, gates on ICP intent via condition, discovers technical contacts via API, validates emails via API, drafts a technical outreach with AI, and creates a CRM record plus alert in HubSpot and Slack.",
    nodes: [
      trigger("Webhook: Documentation page visitor", "webhook_app", {
        source: "website_analytics",
        event: "docs.visitor_identified",
        howItStarts:
          "Fires when an identified visitor views documentation pages (API docs, integration guides, setup instructions) — a strong technical evaluation signal indicating active product research",
        setup:
          "Configure website analytics webhook for docs page visitor identification. Set Clearbit API key for company qualification. Set Hunter.io API key for contact discovery. Set ZeroBounce API key for email validation. Configure OpenAI for outreach drafting. Set HubSpot token and Slack webhook.",
        inputFields: [],
      }),

      // 2. AI extract visitor data
      aiNode(
        "2",
        "AI Extract Docs Visitor Data",
        280,
        "You are a website analytics data parser. Extract the visitor email, visitor name, company domain, pages visited (list), total page views, session duration in seconds, referrer source, the specific docs section visited (API, integrations, setup, pricing), UTM parameters, and the visitor IP or location from the raw webhook payload. Respond JSON: {visitor_email: string, visitor_name: string, company_domain: string, pages_visited: string, page_view_count: string, session_duration_seconds: string, referrer_source: string, docs_section: string, utm_source: string, utm_campaign: string, visitor_location: string, visit_timestamp: string}",
        "Raw webhook payload: {webhook_payload}. Extract all documentation visitor data regardless of field naming conventions.",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "pages_visited", as: "pages_visited", type: "string" },
          { field: "page_view_count", as: "page_view_count", type: "string" },
          {
            field: "session_duration_seconds",
            as: "session_duration_seconds",
            type: "string",
          },
          { field: "referrer_source", as: "referrer_source", type: "string" },
          { field: "docs_section", as: "docs_section", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "visitor_location", as: "visitor_location", type: "string" },
          { field: "visit_timestamp", as: "visit_timestamp", type: "string" },
        ],
      ),

      // 3. Stage assessment runCode
      n("3", "runCode", "Assess Visitor Stage and Intent", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assess documentation visitor stage and intent strength
const pageViews = parseInt(vars.page_view_count) || 1;
const sessionSec = parseInt(vars.session_duration_seconds) || 0;
const docsSection = (vars.docs_section || "").toLowerCase();
const pages = (vars.pages_visited || "").toLowerCase();
let intentScore = 0;
intentScore += pageViews >= 5 ? 30 : pageViews >= 3 ? 20 : 10;
intentScore += sessionSec > 300 ? 25 : sessionSec > 120 ? 15 : 5;
if (docsSection.includes("api")) intentScore += 20;
if (docsSection.includes("integration")) intentScore += 15;
if (docsSection.includes("pricing")) intentScore += 20;
if (docsSection.includes("setup")) intentScore += 10;
if (pages.includes("authentication") || pages.includes("webhook")) intentScore += 10;
const visitorStage = intentScore >= 60 ? "evaluation" : intentScore >= 35 ? "research" : "awareness";
const isHighIntent = intentScore >= 50 ? "true" : "false";
const sessionMinutes = Math.round(sessionSec / 60);
return {
  intent_score: Math.min(intentScore, 100),
  visitor_stage: visitorStage,
  is_high_intent: isHighIntent,
  session_minutes: sessionMinutes,
  primary_interest: docsSection || "general_docs",
  engagement_summary: pageViews + " pages in " + sessionMinutes + " min | section: " + docsSection
};`,
          outputVariables: [
            { name: "intent_score", type: "number" },
            { name: "visitor_stage", type: "string" },
            { name: "is_high_intent", type: "string" },
            { name: "session_minutes", type: "number" },
            { name: "primary_interest", type: "string" },
            { name: "engagement_summary", type: "string" },
          ],
        },
      }),

      // 4. Company qualification Clearbit API
      n("4", "hitApi", "Qualify Company via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
            {
              name: "category.sector",
              customName: "company_sector",
              type: "string",
            },
          ],
        },
      }),

      // 5. ICP intent gate condition
      n("5", "condition", "Is High-Intent ICP Visitor?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_intent", operator: "eq", value: "true" },
          ],
        },
      }),

      // 6. Technical contact discovery API
      n("6", "hitApi", "Discover Technical Contacts via Hunter", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={company_domain}&api_key={hunter_api_key}&department=engineering&limit=3",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "tech_contact_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "tech_contact_first",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "tech_contact_last",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "tech_contact_position",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI technical outreach draft
      aiNode(
        "7",
        "AI Draft Technical Outreach",
        1480,
        "You are a developer-focused sales outreach expert. Given a documentation visitor context including the docs section they explored, their company tech stack, session depth, and the specific pages viewed, craft a technical, value-driven outreach email that references their research and offers relevant technical resources. Avoid being salesy — be helpful and developer-friendly. Respond JSON: {outreach_subject: string, outreach_body_html: string, technical_resource: string, suggested_next_step: string}",
        "Visitor from {company_name} ({company_employees} employees, {company_industry}). Docs section: {primary_interest}. Pages: {pages_visited}. Engagement: {engagement_summary}. Intent: {intent_score}/100 ({visitor_stage}). Tech stack: {company_tech_stack}. Contact: {tech_contact_first} {tech_contact_last} ({tech_contact_position}). Craft the outreach.",
        [
          { field: "outreach_subject", as: "outreach_subject", type: "string" },
          {
            field: "outreach_body_html",
            as: "outreach_body_html",
            type: "string",
          },
          {
            field: "technical_resource",
            as: "technical_resource",
            type: "string",
          },
          {
            field: "suggested_next_step",
            as: "suggested_next_step",
            type: "string",
          },
        ],
      ),

      // 8. CRM record + alert HubSpot + Slack
      hubspotPostNode(
        "8",
        "Create CRM Record + Alert",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{tech_contact_email}","firstname":"{tech_contact_first}","lastname":"{tech_contact_last}","company":"{company_name}","jobtitle":"{tech_contact_position}","industry":"{company_industry}","hs_lead_status":"NEW","lifecyclestage":"lead","notes_last_contacted":"Docs visitor: {engagement_summary}. Interest: {primary_interest}. Intent: {intent_score}/100."}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1778. DUPLICATE MERGE SUGGESTIONS (8 nodes)
  // ===================================================================

  "Duplicate Merge Suggestions": {
    summary:
      "Runs on a daily schedule to extract CRM contacts from HubSpot, clusters by domain with runCode, performs fuzzy name and company matching with runCode, cross-checks phone and address with runCode, scores merge confidence with runCode, gates auto-merge via condition, executes merges and logs an audit trail to Sheets and HubSpot.",
    nodes: [
      trigger("Schedule: Daily duplicate detection", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs daily at 6 AM to scan CRM contacts for potential duplicates using domain clustering, fuzzy name matching, phone cross-checking, and address comparison — ensuring a clean database",
        setup:
          "Configure HubSpot token for contact extraction and merging. Set Google Sheets credentials for audit logging. Review merge threshold settings before enabling auto-merge.",
        inputFields: [],
      }),

      // 2. CRM extraction HubSpot
      hubspotGetNode(
        "2",
        "Extract CRM Contacts from HubSpot",
        280,
        "crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,company,phone,address,city,state,zip,hs_lead_status,createdate",
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "contact_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "contact_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "contact_company",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "contact_phone",
            type: "string",
          },
          {
            name: "results[0].properties.address",
            customName: "contact_address",
            type: "string",
          },
          {
            name: "results[0].properties.createdate",
            customName: "contact_created",
            type: "string",
          },
          { name: "total", customName: "total_contacts", type: "number" },
        ],
      ),

      // 3. Domain clustering runCode
      n("3", "runCode", "Cluster Contacts by Domain", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Cluster contacts by email domain to find potential duplicates
const email = (vars.contact_email || "").toLowerCase().trim();
const domain = email.split("@")[1] || "";
const localPart = email.split("@")[0] || "";
const isGenericDomain = ["gmail.com","yahoo.com","hotmail.com","outlook.com","aol.com"].includes(domain);
const clusterKey = isGenericDomain ? "generic_" + localPart.replace(/[0-9.+_-]/g, "") : domain;
const normalizedName = ((vars.contact_first || "") + " " + (vars.contact_last || "")).trim().toLowerCase().replace(/[^a-z ]/g, "");
const normalizedCompany = (vars.contact_company || "").trim().toLowerCase().replace(/[^a-z0-9 ]/g, "");
return {
  email_domain: domain,
  cluster_key: clusterKey,
  is_generic_domain: isGenericDomain ? "true" : "false",
  normalized_name: normalizedName,
  normalized_company: normalizedCompany,
  local_part: localPart,
  dedup_fingerprint: normalizedName + "|" + normalizedCompany + "|" + domain
};`,
          outputVariables: [
            { name: "email_domain", type: "string" },
            { name: "cluster_key", type: "string" },
            { name: "is_generic_domain", type: "string" },
            { name: "normalized_name", type: "string" },
            { name: "normalized_company", type: "string" },
            { name: "local_part", type: "string" },
            { name: "dedup_fingerprint", type: "string" },
          ],
        },
      }),

      // 4. Fuzzy name company match runCode
      n("4", "runCode", "Fuzzy Match Name and Company", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Perform fuzzy matching on name and company fields
function levenshtein(a, b) {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      matrix[i][j] = b[i-1] === a[j-1] ? matrix[i-1][j-1] : Math.min(matrix[i-1][j-1]+1, matrix[i][j-1]+1, matrix[i-1][j]+1);
    }
  }
  return matrix[b.length][a.length];
}
const name = vars.normalized_name || "";
const company = vars.normalized_company || "";
const nameParts = name.split(" ").filter(Boolean);
const nameInitials = nameParts.map(p => p[0]).join("");
// Similarity metrics
const nameSelfScore = name.length > 0 ? 100 : 0;
const companySelfScore = company.length > 0 ? 100 : 0;
const hasFirstAndLast = nameParts.length >= 2;
const companyAbbreviations = company.replace(/\\s+(inc|llc|ltd|corp|co)\\.?$/i, "").trim();
return {
  name_parts_count: nameParts.length,
  name_initials: nameInitials,
  has_full_name: hasFirstAndLast ? "true" : "false",
  company_normalized: companyAbbreviations,
  name_match_confidence: nameSelfScore,
  company_match_confidence: companySelfScore,
  fuzzy_match_ready: "true"
};`,
          outputVariables: [
            { name: "name_parts_count", type: "number" },
            { name: "name_initials", type: "string" },
            { name: "has_full_name", type: "string" },
            { name: "company_normalized", type: "string" },
            { name: "name_match_confidence", type: "number" },
            { name: "company_match_confidence", type: "number" },
            { name: "fuzzy_match_ready", type: "string" },
          ],
        },
      }),

      // 5. Phone address cross-check runCode
      n("5", "runCode", "Cross-Check Phone and Address", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Cross-check phone and address fields for additional duplicate signals
const phone = (vars.contact_phone || "").replace(/[^0-9]/g, "");
const address = (vars.contact_address || "").toLowerCase().trim();
const normalizedPhone = phone.length >= 10 ? phone.slice(-10) : phone;
const hasPhone = normalizedPhone.length === 10;
const hasAddress = address.length > 5;
const addressParts = address.split(/[,\\s]+/).filter(Boolean);
const streetNumber = addressParts[0] || "";
let crossCheckScore = 0;
if (hasPhone) crossCheckScore += 30;
if (hasAddress) crossCheckScore += 20;
if (vars.has_full_name === "true") crossCheckScore += 25;
if (vars.company_normalized && vars.company_normalized.length > 2) crossCheckScore += 25;
const dataCompleteness = crossCheckScore;
return {
  normalized_phone: normalizedPhone,
  has_phone: hasPhone ? "true" : "false",
  has_address: hasAddress ? "true" : "false",
  street_number: streetNumber,
  cross_check_score: crossCheckScore,
  data_completeness: dataCompleteness,
  phone_fingerprint: normalizedPhone.slice(-4) || "none"
};`,
          outputVariables: [
            { name: "normalized_phone", type: "string" },
            { name: "has_phone", type: "string" },
            { name: "has_address", type: "string" },
            { name: "street_number", type: "string" },
            { name: "cross_check_score", type: "number" },
            { name: "data_completeness", type: "number" },
            { name: "phone_fingerprint", type: "string" },
          ],
        },
      }),

      // 6. Merge scoring runCode
      n("6", "runCode", "Score Merge Confidence", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute overall merge confidence score
const nameConf = parseInt(vars.name_match_confidence) || 0;
const companyConf = parseInt(vars.company_match_confidence) || 0;
const crossCheck = parseInt(vars.cross_check_score) || 0;
const isGeneric = vars.is_generic_domain === "true";
let mergeScore = 0;
mergeScore += nameConf > 0 ? 25 : 0;
mergeScore += companyConf > 0 ? 25 : 0;
mergeScore += crossCheck >= 50 ? 25 : crossCheck >= 25 ? 15 : 5;
if (!isGeneric) mergeScore += 25;
const mergeRecommendation = mergeScore >= 80 ? "auto_merge" : mergeScore >= 50 ? "review_suggested" : "no_merge";
const canAutoMerge = mergeRecommendation === "auto_merge" ? "true" : "false";
return {
  merge_confidence_score: mergeScore,
  merge_recommendation: mergeRecommendation,
  can_auto_merge: canAutoMerge,
  merge_reason: "name:" + nameConf + " company:" + companyConf + " cross:" + crossCheck + " domain:" + (isGeneric ? "generic" : "corporate"),
  risk_level: mergeScore >= 80 ? "low" : mergeScore >= 50 ? "medium" : "high"
};`,
          outputVariables: [
            { name: "merge_confidence_score", type: "number" },
            { name: "merge_recommendation", type: "string" },
            { name: "can_auto_merge", type: "string" },
            { name: "merge_reason", type: "string" },
            { name: "risk_level", type: "string" },
          ],
        },
      }),

      // 7. Auto-merge gate condition
      n("7", "condition", "Can Auto-Merge?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "can_auto_merge", operator: "eq", value: "true" },
          ],
        },
      }),

      // 8. Merge execution + audit Sheets HubSpot
      sheetsNode(
        "8",
        "Log Merge Audit Trail to Sheets",
        1720,
        "DuplicateAudit",
        '[["{contact_id}","{contact_email}","{normalized_name}","{company_normalized}","{dedup_fingerprint}","{merge_confidence_score}","{merge_recommendation}","{merge_reason}","{risk_level}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1779. ECOMMERCE PROMO STORES PIPELINE (8 nodes)
  // ===================================================================

  "Ecommerce Promo Stores Pipeline": {
    summary:
      "Runs on a weekly schedule to discover promotions via API, analyzes promo strategy with runCode, enriches store data via Clearbit API, scores service fit via condition, discovers store contacts via API, drafts a pitch with AI, sends outreach email, and creates a CRM entry in HubSpot.",
    nodes: [
      trigger("Schedule: Weekly promo store discovery", "schedule", {
        schedule: "0 8 * * 2",
        howItStarts:
          "Runs weekly on Tuesday mornings to discover ecommerce stores running active promotions that could benefit from marketing automation services — targets stores with significant promotional activity as pipeline opportunities",
        setup:
          "Configure promotional discovery API (BuiltWith, SimilarWeb). Set Clearbit API key for store enrichment. Set Hunter.io API key for contact discovery. Configure OpenAI API key for pitch generation. Set HubSpot token for pipeline creation. Configure email credentials.",
        inputFields: [],
      }),

      // 2. Promotion discovery API
      n("2", "hitApi", "Discover Active Promotions via API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.builtwith.com/v20/api.json?KEY={builtwith_api_key}&LOOKUP=ecommerce&TECH=shopify,woocommerce,magento",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "Results[0].Domain",
              customName: "store_domain",
              type: "string",
            },
            {
              name: "Results[0].Meta.CompanyName",
              customName: "store_name",
              type: "string",
            },
            {
              name: "Results[0].Technologies[0].Tag",
              customName: "ecommerce_platform",
              type: "string",
            },
            {
              name: "Results[0].Meta.Vertical",
              customName: "store_vertical",
              type: "string",
            },
            {
              name: "Results[0].Meta.Telephones[0]",
              customName: "store_phone",
              type: "string",
            },
          ],
        },
      }),

      // 3. Promo-strategy analysis runCode
      n("3", "runCode", "Analyze Promo Strategy", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze the store's promotional strategy and identify service opportunities
const platform = (vars.ecommerce_platform || "").toLowerCase();
const vertical = (vars.store_vertical || "").toLowerCase();
const domain = vars.store_domain || "";
const platformScore = platform.includes("shopify") ? 85 : platform.includes("woocommerce") ? 70 : platform.includes("magento") ? 75 : 50;
const opportunities = [];
if (platformScore >= 70) opportunities.push("email_automation");
opportunities.push("abandoned_cart_recovery");
opportunities.push("post_purchase_nurture");
if (vertical.includes("fashion") || vertical.includes("apparel")) opportunities.push("seasonal_campaigns");
if (vertical.includes("food") || vertical.includes("health")) opportunities.push("subscription_nurture");
opportunities.push("loyalty_program_automation");
const fitScore = Math.min(platformScore + (opportunities.length * 5), 100);
const isServiceFit = fitScore >= 65 ? "true" : "false";
return {
  platform_score: platformScore,
  service_opportunities: opportunities.join(", "),
  opportunity_count: opportunities.length,
  fit_score: fitScore,
  is_service_fit: isServiceFit,
  primary_opportunity: opportunities[0] || "general_automation",
  store_tier: fitScore >= 80 ? "high_value" : fitScore >= 60 ? "mid_value" : "low_value"
};`,
          outputVariables: [
            { name: "platform_score", type: "number" },
            { name: "service_opportunities", type: "string" },
            { name: "opportunity_count", type: "number" },
            { name: "fit_score", type: "number" },
            { name: "is_service_fit", type: "string" },
            { name: "primary_opportunity", type: "string" },
            { name: "store_tier", type: "string" },
          ],
        },
      }),

      // 4. Store enrichment Clearbit API
      n("4", "hitApi", "Enrich Store via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={store_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "enriched_store_name", type: "string" },
            {
              name: "metrics.employees",
              customName: "store_employees",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "store_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "store_industry",
              type: "string",
            },
            {
              name: "geo.country",
              customName: "store_country",
              type: "string",
            },
          ],
        },
      }),

      // 5. Service-fit scoring condition
      n("5", "condition", "Is Service Fit Score Adequate?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_service_fit", operator: "eq", value: "true" },
          ],
        },
      }),

      // 6. Contact discovery API
      n("6", "hitApi", "Discover Store Contacts via Hunter", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={store_domain}&api_key={hunter_api_key}&department=marketing&limit=3",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "store_contact_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "store_contact_first",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "store_contact_last",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "store_contact_position",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI pitch draft
      aiNode(
        "7",
        "AI Draft Ecommerce Pitch",
        1480,
        "You are a B2B ecommerce marketing sales specialist. Given a store running on a known ecommerce platform with identified service opportunities, craft a compelling outreach pitch that references their platform, identifies quick wins, and proposes specific automation solutions. Be specific about ROI potential. Respond JSON: {pitch_subject: string, pitch_body_html: string, quick_wins: string, roi_estimate: string, case_study_reference: string}",
        "Store: {enriched_store_name} ({store_domain}), Platform: {ecommerce_platform}, Revenue: {store_revenue}, Employees: {store_employees}, Industry: {store_industry}. Opportunities: {service_opportunities}. Primary: {primary_opportunity}. Fit: {fit_score}/100 ({store_tier}). Contact: {store_contact_first} {store_contact_last} ({store_contact_position}). Craft the pitch.",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body_html", as: "pitch_body_html", type: "string" },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          { field: "roi_estimate", as: "roi_estimate", type: "string" },
          {
            field: "case_study_reference",
            as: "case_study_reference",
            type: "string",
          },
        ],
      ),

      // 8. Send + CRM entry HubSpot
      hubspotPostNode(
        "8",
        "Create Pipeline Entry in HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{store_contact_email}","firstname":"{store_contact_first}","lastname":"{store_contact_last}","company":"{enriched_store_name}","jobtitle":"{store_contact_position}","industry":"{store_industry}","hs_lead_status":"NEW","lifecyclestage":"lead","numberofemployees":"{store_employees}","notes_last_contacted":"Ecommerce promo outreach: {primary_opportunity}. Fit: {fit_score}/100. Platform: {ecommerce_platform}. Tier: {store_tier}."}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1780. ECOSYSTEM OVERLAP VIA CROSSBEAM (8 nodes)
  // ===================================================================

  "Ecosystem Overlap via Crossbeam": {
    summary:
      "Runs on a weekly schedule to pull partner overlap data from Crossbeam API, classifies overlaps with runCode, estimates revenue potential with runCode, ranks priorities with runCode, maps partner contacts with runCode, generates a co-sell brief with AI, and sends partner outreach email plus internal alert to Slack and HubSpot.",
    nodes: [
      trigger("Schedule: Weekly Crossbeam overlap analysis", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs weekly on Monday mornings to analyze partner ecosystem overlaps from Crossbeam — identifying co-sell opportunities, mutual customers, and revenue potential across the partner network",
        setup:
          "Configure Crossbeam API key for overlap data. Set OpenAI API key for co-sell brief generation. Configure email credentials for partner outreach. Set Slack webhook for internal alerts. Set HubSpot token for CRM updates.",
        inputFields: [],
      }),

      // 2. Crossbeam overlap pull API
      n("2", "hitApi", "Pull Overlap Data from Crossbeam", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.crossbeam.com/v1/overlaps?status=active&limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {crossbeam_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].partner_name",
              customName: "partner_name",
              type: "string",
            },
            {
              name: "data[0].partner_domain",
              customName: "partner_domain",
              type: "string",
            },
            {
              name: "data[0].overlap_type",
              customName: "overlap_type",
              type: "string",
            },
            {
              name: "data[0].account_name",
              customName: "overlap_account",
              type: "string",
            },
            {
              name: "data[0].account_domain",
              customName: "overlap_domain",
              type: "string",
            },
            {
              name: "data[0].our_stage",
              customName: "our_deal_stage",
              type: "string",
            },
            {
              name: "data[0].partner_stage",
              customName: "partner_deal_stage",
              type: "string",
            },
            {
              name: "data[0].our_amount",
              customName: "our_deal_amount",
              type: "string",
            },
            {
              name: "meta.total",
              customName: "total_overlaps",
              type: "number",
            },
          ],
        },
      }),

      // 3. Overlap classification runCode
      n("3", "runCode", "Classify Overlap Type", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify overlap for co-sell strategy
const overlapType = (vars.overlap_type || "").toLowerCase();
const ourStage = (vars.our_deal_stage || "").toLowerCase();
const partnerStage = (vars.partner_deal_stage || "").toLowerCase();
let classification = "informational";
let coSellStrategy = "none";
if (overlapType.includes("prospect_prospect")) {
  classification = "co_prospect";
  coSellStrategy = "joint_outreach";
} else if (overlapType.includes("prospect_customer")) {
  classification = "warm_intro";
  coSellStrategy = "partner_referral";
} else if (overlapType.includes("customer_prospect")) {
  classification = "reverse_referral";
  coSellStrategy = "customer_intro";
} else if (overlapType.includes("customer_customer")) {
  classification = "mutual_customer";
  coSellStrategy = "case_study_collaboration";
}
const isActiveOpportunity = ourStage.includes("qualified") || ourStage.includes("presentation") || ourStage.includes("negotiation");
const priorityBoost = isActiveOpportunity ? 20 : 0;
return {
  overlap_classification: classification,
  co_sell_strategy: coSellStrategy,
  is_active_opportunity: isActiveOpportunity ? "true" : "false",
  priority_boost: priorityBoost,
  overlap_summary: classification + " | " + coSellStrategy + " | our:" + ourStage + " partner:" + partnerStage
};`,
          outputVariables: [
            { name: "overlap_classification", type: "string" },
            { name: "co_sell_strategy", type: "string" },
            { name: "is_active_opportunity", type: "string" },
            { name: "priority_boost", type: "number" },
            { name: "overlap_summary", type: "string" },
          ],
        },
      }),

      // 4. Revenue estimation runCode
      n("4", "runCode", "Estimate Revenue Potential", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Estimate co-sell revenue potential
const ourAmount = parseFloat(vars.our_deal_amount) || 0;
const classification = vars.overlap_classification || "";
const isActive = vars.is_active_opportunity === "true";
let revenueMultiplier = 1.0;
if (classification === "warm_intro") revenueMultiplier = 1.5;
else if (classification === "co_prospect") revenueMultiplier = 1.2;
else if (classification === "reverse_referral") revenueMultiplier = 0.8;
else if (classification === "mutual_customer") revenueMultiplier = 0.5;
const estimatedRevenue = Math.round(ourAmount * revenueMultiplier);
const winRateBoost = classification === "warm_intro" ? 25 : classification === "co_prospect" ? 15 : 5;
const partnerInfluenceScore = Math.min(100, (winRateBoost * 2) + (isActive ? 30 : 0) + (ourAmount > 50000 ? 20 : 10));
return {
  estimated_revenue: estimatedRevenue,
  revenue_multiplier: revenueMultiplier,
  win_rate_boost_percent: winRateBoost,
  partner_influence_score: partnerInfluenceScore,
  revenue_category: estimatedRevenue > 100000 ? "enterprise" : estimatedRevenue > 25000 ? "mid_market" : "smb"
};`,
          outputVariables: [
            { name: "estimated_revenue", type: "number" },
            { name: "revenue_multiplier", type: "number" },
            { name: "win_rate_boost_percent", type: "number" },
            { name: "partner_influence_score", type: "number" },
            { name: "revenue_category", type: "string" },
          ],
        },
      }),

      // 5. Priority ranking runCode
      n("5", "runCode", "Rank Overlap Priority", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute final priority ranking
const influence = parseInt(vars.partner_influence_score) || 0;
const boost = parseInt(vars.priority_boost) || 0;
const revenue = parseInt(vars.estimated_revenue) || 0;
const isActive = vars.is_active_opportunity === "true";
let priorityScore = influence + boost;
if (revenue > 50000) priorityScore += 15;
if (isActive) priorityScore += 20;
const priorityRank = priorityScore >= 80 ? "P0_immediate" : priorityScore >= 55 ? "P1_this_week" : priorityScore >= 30 ? "P2_this_month" : "P3_backlog";
const actionNeeded = priorityRank.startsWith("P0") || priorityRank.startsWith("P1") ? "true" : "false";
return {
  priority_score: Math.min(priorityScore, 100),
  priority_rank: priorityRank,
  action_needed: actionNeeded,
  ranking_factors: "influence:" + influence + " boost:" + boost + " revenue:" + revenue + " active:" + isActive
};`,
          outputVariables: [
            { name: "priority_score", type: "number" },
            { name: "priority_rank", type: "string" },
            { name: "action_needed", type: "string" },
            { name: "ranking_factors", type: "string" },
          ],
        },
      }),

      // 6. Partner contact mapping runCode
      n("6", "runCode", "Map Partner Contacts", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map partner contacts for outreach
const partnerName = vars.partner_name || "Unknown Partner";
const partnerDomain = vars.partner_domain || "";
const strategy = vars.co_sell_strategy || "none";
const partnerEmail = "partnerships@" + partnerDomain;
const partnerSlack = "#partner-" + partnerName.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-");
const outreachTemplate = strategy === "joint_outreach" ? "co_sell_intro" : strategy === "partner_referral" ? "warm_intro_request" : strategy === "customer_intro" ? "reverse_intro" : "general_partner_update";
return {
  partner_contact_email: partnerEmail,
  partner_slack_channel: partnerSlack,
  outreach_template: outreachTemplate,
  partner_display: partnerName + " (" + partnerDomain + ")"
};`,
          outputVariables: [
            { name: "partner_contact_email", type: "string" },
            { name: "partner_slack_channel", type: "string" },
            { name: "outreach_template", type: "string" },
            { name: "partner_display", type: "string" },
          ],
        },
      }),

      // 7. AI co-sell brief generation
      aiNode(
        "7",
        "AI Generate Co-Sell Brief",
        1480,
        "You are a partner ecosystem and co-sell strategist. Given overlap data including classification, revenue potential, priority ranking, and partner details, generate a concise co-sell brief that includes a recommended approach, key talking points, mutual benefits, and a specific ask for the partner. Respond JSON: {brief_summary: string, recommended_approach: string, talking_points: string, mutual_benefits: string, specific_ask: string, outreach_email_html: string}",
        "Partner: {partner_display}. Overlap account: {overlap_account} ({overlap_domain}). Classification: {overlap_classification}. Strategy: {co_sell_strategy}. Revenue: ${estimated_revenue} ({revenue_category}). Win rate boost: +{win_rate_boost_percent}%. Priority: {priority_rank} ({priority_score}/100). Our stage: {our_deal_stage}. Partner stage: {partner_deal_stage}. Generate the co-sell brief.",
        [
          { field: "brief_summary", as: "brief_summary", type: "string" },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "mutual_benefits", as: "mutual_benefits", type: "string" },
          { field: "specific_ask", as: "specific_ask", type: "string" },
          {
            field: "outreach_email_html",
            as: "outreach_email_html",
            type: "string",
          },
        ],
      ),

      // 8. Partner outreach + internal alert Slack HubSpot
      slackNode(
        "8",
        "Send Co-Sell Alert + Partner Outreach",
        1720,
        "ECOSYSTEM OVERLAP: *{overlap_account}*\\nPartner: {partner_display} | Type: {overlap_classification}\\nStrategy: {co_sell_strategy} | Priority: {priority_rank}\\nRevenue: ${estimated_revenue} ({revenue_category}) | Win boost: +{win_rate_boost_percent}%\\nBrief: {brief_summary}\\nApproach: {recommended_approach}\\nAsk: {specific_ask}\\nPartner contact: {partner_contact_email}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1781. EMAIL HYGIENE BATCH PROCESSOR (8 nodes)
  // ===================================================================

  "Email Hygiene Batch Processor": {
    summary:
      "Runs on a weekly schedule to extract CRM emails from HubSpot, validates via ZeroBounce batch API, classifies results with runCode, runs secondary verification via NeverBounce API, updates sendable contacts in HubSpot, updates risky/invalid contacts in HubSpot, and generates a hygiene report in Sheets.",
    nodes: [
      trigger("Schedule: Weekly email hygiene run", "schedule", {
        schedule: "0 3 * * 0",
        howItStarts:
          "Runs weekly on Sunday at 3 AM to process CRM email addresses through validation and hygiene checks — ensuring the database maintains high deliverability standards and minimizing bounce risk",
        setup:
          "Configure HubSpot token for contact extraction and batch updates. Set ZeroBounce API key for primary validation. Set NeverBounce API key for secondary verification. Configure Google Sheets credentials for reporting.",
        inputFields: [],
      }),

      // 2. CRM email extraction HubSpot
      hubspotGetNode(
        "2",
        "Extract Emails from HubSpot CRM",
        280,
        "crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,hs_email_domain,hs_email_sends_since_last_engagement,hs_email_bounce",
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_domain",
            customName: "email_domain",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_bounce",
            customName: "previous_bounce",
            type: "string",
          },
          {
            name: "total",
            customName: "total_contacts_pulled",
            type: "number",
          },
        ],
      ),

      // 3. ZeroBounce batch validation API
      n("3", "hitApi", "Validate via ZeroBounce Batch API", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://bulkapi.zerobounce.net/v2/validatebatch",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{zerobounce_api_key}" },
            {
              key: "email_batch",
              value: '[{"email_address":"{contact_email}"}]',
            },
          ],
          variables: [
            {
              name: "email_batch[0].status",
              customName: "zb_status",
              type: "string",
            },
            {
              name: "email_batch[0].sub_status",
              customName: "zb_sub_status",
              type: "string",
            },
            {
              name: "email_batch[0].free_email",
              customName: "zb_free_email",
              type: "string",
            },
            {
              name: "email_batch[0].did_you_mean",
              customName: "zb_suggestion",
              type: "string",
            },
            {
              name: "email_batch[0].domain_age_days",
              customName: "domain_age_days",
              type: "string",
            },
          ],
        },
      }),

      // 4. Classification engine runCode
      n("4", "runCode", "Classify Validation Results", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify email validation results into actionable categories
const status = (vars.zb_status || "").toLowerCase();
const subStatus = (vars.zb_sub_status || "").toLowerCase();
const isFree = vars.zb_free_email === "true";
const hasSuggestion = (vars.zb_suggestion || "").length > 0;
const domainAge = parseInt(vars.domain_age_days) || 0;
const hadPreviousBounce = (vars.previous_bounce || "0") !== "0";
let category = "unknown";
let action = "hold";
let riskScore = 50;
if (status === "valid") {
  category = "sendable";
  action = "keep";
  riskScore = isFree ? 25 : 10;
} else if (status === "invalid") {
  category = "invalid";
  action = "suppress";
  riskScore = 95;
} else if (status === "catch-all") {
  category = "risky_catchall";
  action = "secondary_verify";
  riskScore = 60;
} else if (status === "spamtrap") {
  category = "spamtrap";
  action = "immediate_suppress";
  riskScore = 100;
} else if (status === "abuse") {
  category = "abuse";
  action = "suppress";
  riskScore = 90;
} else if (status === "do_not_mail") {
  category = "do_not_mail";
  action = "suppress";
  riskScore = 85;
} else {
  category = "unknown";
  action = "secondary_verify";
  riskScore = 50;
}
if (hadPreviousBounce && category === "sendable") riskScore += 15;
if (domainAge < 30 && category === "sendable") riskScore += 10;
const needsSecondaryVerification = action === "secondary_verify" ? "true" : "false";
return {
  hygiene_category: category,
  hygiene_action: action,
  risk_score: Math.min(riskScore, 100),
  needs_secondary: needsSecondaryVerification,
  is_sendable: category === "sendable" ? "true" : "false",
  suggestion: vars.zb_suggestion || "none",
  classification_detail: status + "/" + subStatus + " free:" + isFree + " bounce:" + hadPreviousBounce
};`,
          outputVariables: [
            { name: "hygiene_category", type: "string" },
            { name: "hygiene_action", type: "string" },
            { name: "risk_score", type: "number" },
            { name: "needs_secondary", type: "string" },
            { name: "is_sendable", type: "string" },
            { name: "suggestion", type: "string" },
            { name: "classification_detail", type: "string" },
          ],
        },
      }),

      // 5. Secondary verification NeverBounce API
      n("5", "hitApi", "Secondary Verify via NeverBounce", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.neverbounce.com/v4/single/check?key={neverbounce_api_key}&email={contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "result", customName: "nb_result", type: "string" },
            { name: "flags", customName: "nb_flags", type: "string" },
            {
              name: "suggested_correction",
              customName: "nb_correction",
              type: "string",
            },
          ],
        },
      }),

      // 6. CRM update sendable HubSpot
      hubspotPostNode(
        "6",
        "Update Sendable Contacts in HubSpot",
        1240,
        "crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_email_optout":"false","notes_last_updated":"Email hygiene: {hygiene_category} (risk: {risk_score}/100). ZB: {zb_status}/{zb_sub_status}. NB: {nb_result}. Action: {hygiene_action}."}',
          },
        ],
        [{ name: "id", customName: "updated_sendable_id", type: "string" }],
      ),

      // 7. CRM update risky/invalid HubSpot
      hubspotPostNode(
        "7",
        "Update Risky/Invalid Contacts in HubSpot",
        1480,
        "crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_email_optout":"true","hs_lead_status":"UNQUALIFIED","notes_last_updated":"Email hygiene SUPPRESSED: {hygiene_category} (risk: {risk_score}/100). ZB: {zb_status}/{zb_sub_status}. Suggestion: {suggestion}."}',
          },
        ],
        [{ name: "id", customName: "updated_risky_id", type: "string" }],
      ),

      // 8. Hygiene report Sheets
      sheetsNode(
        "8",
        "Log Hygiene Report to Sheets",
        1720,
        "EmailHygiene",
        '[["{contact_id}","{contact_email}","{first_name} {last_name}","{zb_status}","{zb_sub_status}","{nb_result}","{hygiene_category}","{hygiene_action}","{risk_score}","{suggestion}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1782. EMAIL VALIDATION WATERFALL (7 nodes)
  // ===================================================================

  "Email Validation Waterfall": {
    summary:
      "Fires when an email ingestion event arrives via webhook, extracts the email with AI, validates via ZeroBounce as primary, triages via condition, runs secondary validation via NeverBounce, scores consensus with runCode, updates CRM in HubSpot, and handles errors with runCode.",
    nodes: [
      trigger("Webhook: Email ingested for validation", "webhook_app", {
        source: "lead_intake",
        event: "email.ingested",
        howItStarts:
          "Fires when a new email address enters the system from any source (form submission, import, API ingestion) — triggers a multi-provider validation waterfall to ensure only verified emails enter the CRM",
        setup:
          "Configure lead intake webhook for email ingestion events. Set OpenAI API key for extraction. Set ZeroBounce API key for primary validation. Set NeverBounce API key for secondary validation. Configure HubSpot token for CRM updates.",
        inputFields: [],
      }),

      // 2. AI extract email data
      aiNode(
        "2",
        "AI Extract Email for Validation",
        280,
        "You are a lead intake data parser. Extract the email address to validate, the associated contact name, the source of the email (form, import, API, manual), any company name, and a reference ID or batch ID from the raw webhook payload. Respond JSON: {email_to_validate: string, contact_name: string, email_source: string, company_name: string, reference_id: string, ingestion_timestamp: string}",
        "Raw webhook payload: {webhook_payload}. Extract the email and context data regardless of field naming conventions.",
        [
          {
            field: "email_to_validate",
            as: "email_to_validate",
            type: "string",
          },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "email_source", as: "email_source", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "reference_id", as: "reference_id", type: "string" },
          {
            field: "ingestion_timestamp",
            as: "ingestion_timestamp",
            type: "string",
          },
        ],
      ),

      // 3. Primary validation ZeroBounce API
      n("3", "hitApi", "Primary Validate via ZeroBounce", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={email_to_validate}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "zb_status", type: "string" },
            { name: "sub_status", customName: "zb_sub_status", type: "string" },
            { name: "free_email", customName: "zb_free_email", type: "string" },
            {
              name: "did_you_mean",
              customName: "zb_suggestion",
              type: "string",
            },
            { name: "mx_found", customName: "zb_mx_found", type: "string" },
            {
              name: "smtp_provider",
              customName: "zb_smtp_provider",
              type: "string",
            },
          ],
        },
      }),

      // 4. Triage gate condition
      n("4", "condition", "Needs Secondary Validation?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "zb_status", operator: "eq", value: "catch-all" },
          ],
        },
      }),

      // 5. Secondary validation NeverBounce API
      n("5", "hitApi", "Secondary Validate via NeverBounce", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.neverbounce.com/v4/single/check?key={neverbounce_api_key}&email={email_to_validate}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "result", customName: "nb_result", type: "string" },
            { name: "flags", customName: "nb_flags", type: "string" },
            {
              name: "suggested_correction",
              customName: "nb_correction",
              type: "string",
            },
          ],
        },
      }),

      // 6. Consensus scoring runCode
      n("6", "runCode", "Score Validation Consensus", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute consensus validation score from multiple providers
const zbStatus = (vars.zb_status || "").toLowerCase();
const nbResult = (vars.nb_result || "").toLowerCase();
const mxFound = vars.zb_mx_found === "true";
const isFree = vars.zb_free_email === "true";
let zbScore = 0;
if (zbStatus === "valid") zbScore = 100;
else if (zbStatus === "catch-all") zbScore = 50;
else if (zbStatus === "unknown") zbScore = 30;
else zbScore = 0;
let nbScore = 0;
if (nbResult === "valid") nbScore = 100;
else if (nbResult === "unknown") nbScore = 40;
else if (nbResult === "disposable") nbScore = 10;
else nbScore = 0;
const consensusScore = Math.round((zbScore * 0.6) + (nbScore * 0.4));
const finalVerdict = consensusScore >= 70 ? "valid" : consensusScore >= 40 ? "risky" : "invalid";
const shouldAddToCrm = finalVerdict === "valid" ? "true" : "false";
const correction = vars.zb_suggestion || vars.nb_correction || "";
return {
  zb_score: zbScore,
  nb_score: nbScore,
  consensus_score: consensusScore,
  final_verdict: finalVerdict,
  should_add_to_crm: shouldAddToCrm,
  suggested_correction: correction || "none",
  provider_detail: "ZB:" + zbStatus + "(" + zbScore + ") NB:" + nbResult + "(" + nbScore + ") MX:" + mxFound
};`,
          outputVariables: [
            { name: "zb_score", type: "number" },
            { name: "nb_score", type: "number" },
            { name: "consensus_score", type: "number" },
            { name: "final_verdict", type: "string" },
            { name: "should_add_to_crm", type: "string" },
            { name: "suggested_correction", type: "string" },
            { name: "provider_detail", type: "string" },
          ],
        },
      }),

      // 7. CRM update HubSpot
      hubspotPostNode(
        "7",
        "Update CRM Validation Status",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{email_to_validate}","firstname":"{contact_name}","company":"{company_name}","hs_lead_status":"{final_verdict}","notes_last_contacted":"Email validation: {final_verdict} (consensus: {consensus_score}/100). {provider_detail}. Source: {email_source}. Ref: {reference_id}."}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "crm_created",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1783. ENRICHMENT AUDIT COVERAGE REPORT (7 nodes)
  // ===================================================================

  "Enrichment Audit Coverage Report": {
    summary:
      "Runs on a weekly schedule to pull a CRM schema sample from HubSpot, calculates fill rates with runCode, scores data quality with runCode, prioritizes gaps with runCode, generates source recommendations with AI, and delivers a coverage report via email and Slack.",
    nodes: [
      trigger("Schedule: Weekly enrichment audit", "schedule", {
        schedule: "0 7 * * 1",
        howItStarts:
          "Runs weekly on Monday mornings to audit CRM data enrichment coverage — analyzing fill rates across all contact properties, scoring data quality, identifying gaps, and recommending enrichment sources to close coverage holes",
        setup:
          "Configure HubSpot token for schema sampling. Set OpenAI API key for source recommendations. Configure Slack webhook for report delivery. Configure email credentials for detailed report distribution.",
        inputFields: [],
      }),

      // 2. Schema sample pull HubSpot
      hubspotGetNode(
        "2",
        "Pull CRM Schema Sample from HubSpot",
        280,
        "crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,company,jobtitle,phone,address,city,state,zip,industry,numberofemployees,annualrevenue,hs_lead_status,lifecyclestage,hs_analytics_source",
        [
          { name: "results.length", customName: "sample_size", type: "number" },
          {
            name: "results[0].properties.email",
            customName: "sample_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "sample_firstname",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "sample_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "sample_jobtitle",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "sample_phone",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "sample_industry",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "sample_employees",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "sample_revenue",
            type: "string",
          },
        ],
      ),

      // 3. Fill-rate calculation runCode
      n("3", "runCode", "Calculate Field Fill Rates", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate fill rates for critical CRM fields
const fields = {
  email: vars.sample_email,
  first_name: vars.sample_firstname,
  company: vars.sample_company,
  job_title: vars.sample_jobtitle,
  phone: vars.sample_phone,
  industry: vars.sample_industry,
  employee_count: vars.sample_employees,
  revenue: vars.sample_revenue
};
const sampleSize = parseInt(vars.sample_size) || 1;
let filledCount = 0;
const fieldReport = [];
for (const [field, value] of Object.entries(fields)) {
  const isFilled = value && value.trim && value.trim().length > 0;
  if (isFilled) filledCount++;
  fieldReport.push(field + ":" + (isFilled ? "filled" : "EMPTY"));
}
const overallFillRate = Math.round((filledCount / Object.keys(fields).length) * 100);
const emptyFields = fieldReport.filter(f => f.includes("EMPTY")).map(f => f.split(":")[0]);
const filledFields = fieldReport.filter(f => f.includes("filled")).map(f => f.split(":")[0]);
return {
  overall_fill_rate: overallFillRate,
  filled_count: filledCount,
  total_fields_checked: Object.keys(fields).length,
  empty_fields: emptyFields.join(", ") || "none",
  filled_fields: filledFields.join(", "),
  field_report: fieldReport.join(" | "),
  coverage_grade: overallFillRate >= 80 ? "A" : overallFillRate >= 60 ? "B" : overallFillRate >= 40 ? "C" : "D"
};`,
          outputVariables: [
            { name: "overall_fill_rate", type: "number" },
            { name: "filled_count", type: "number" },
            { name: "total_fields_checked", type: "number" },
            { name: "empty_fields", type: "string" },
            { name: "filled_fields", type: "string" },
            { name: "field_report", type: "string" },
            { name: "coverage_grade", type: "string" },
          ],
        },
      }),

      // 4. Data-quality scoring runCode
      n("4", "runCode", "Score Data Quality", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score data quality beyond just fill rates
const fillRate = parseInt(vars.overall_fill_rate) || 0;
const emptyFields = (vars.empty_fields || "none").split(",").map(f => f.trim()).filter(f => f !== "none");
let qualityScore = fillRate;
// Penalize for missing critical fields
const criticalFields = ["email", "company", "first_name"];
const missingCritical = criticalFields.filter(f => emptyFields.includes(f));
qualityScore -= missingCritical.length * 15;
// Bonus for having enrichment fields filled
const enrichmentFields = ["industry", "employee_count", "revenue", "job_title"];
const filledEnrichment = enrichmentFields.filter(f => !emptyFields.includes(f));
qualityScore += filledEnrichment.length * 5;
qualityScore = Math.max(0, Math.min(qualityScore, 100));
const qualityGrade = qualityScore >= 80 ? "excellent" : qualityScore >= 60 ? "good" : qualityScore >= 40 ? "needs_improvement" : "poor";
return {
  quality_score: qualityScore,
  quality_grade: qualityGrade,
  missing_critical: missingCritical.join(", ") || "none",
  enrichment_coverage: filledEnrichment.length + "/" + enrichmentFields.length,
  priority_gaps: emptyFields.slice(0, 3).join(", ") || "none",
  improvement_potential: Math.min(100 - qualityScore, 100)
};`,
          outputVariables: [
            { name: "quality_score", type: "number" },
            { name: "quality_grade", type: "string" },
            { name: "missing_critical", type: "string" },
            { name: "enrichment_coverage", type: "string" },
            { name: "priority_gaps", type: "string" },
            { name: "improvement_potential", type: "number" },
          ],
        },
      }),

      // 5. Gap prioritization runCode
      n("5", "runCode", "Prioritize Enrichment Gaps", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prioritize gaps by business impact
const gaps = (vars.empty_fields || "").split(",").map(g => g.trim()).filter(Boolean);
const impactMap = { email: 100, company: 90, first_name: 85, job_title: 75, phone: 70, industry: 65, employee_count: 55, revenue: 50 };
const prioritized = gaps.map(g => ({ field: g, impact: impactMap[g] || 30 })).sort((a, b) => b.impact - a.impact);
const topPriority = prioritized[0] || { field: "none", impact: 0 };
const totalImpact = prioritized.reduce((sum, p) => sum + p.impact, 0);
const estimatedCostToFix = prioritized.length * 200;
return {
  prioritized_gaps: prioritized.map(p => p.field + "(" + p.impact + ")").join(", ") || "none",
  top_priority_field: topPriority.field,
  top_priority_impact: topPriority.impact,
  total_gap_impact: totalImpact,
  estimated_fix_cost: estimatedCostToFix,
  gap_count: prioritized.length
};`,
          outputVariables: [
            { name: "prioritized_gaps", type: "string" },
            { name: "top_priority_field", type: "string" },
            { name: "top_priority_impact", type: "number" },
            { name: "total_gap_impact", type: "number" },
            { name: "estimated_fix_cost", type: "number" },
            { name: "gap_count", type: "number" },
          ],
        },
      }),

      // 6. AI source recommendation
      aiNode(
        "6",
        "AI Recommend Enrichment Sources",
        1240,
        "You are a B2B data enrichment strategist. Given a CRM coverage audit with specific field gaps, quality scores, and prioritized needs, recommend the best data enrichment providers and strategies to close the gaps. Consider Clearbit, ZoomInfo, Apollo, Lusha, and other providers. Match specific gaps to the right vendor. Respond JSON: {recommended_providers: string, provider_field_mapping: string, estimated_coverage_improvement: string, implementation_plan: string, budget_estimate: string, quick_wins: string}",
        "Current coverage: {overall_fill_rate}% (Grade: {coverage_grade}). Quality: {quality_score}/100 ({quality_grade}). Missing critical: {missing_critical}. Enrichment coverage: {enrichment_coverage}. Gaps: {prioritized_gaps}. Top priority: {top_priority_field} (impact: {top_priority_impact}). Gap count: {gap_count}. Budget estimate to fix: ${estimated_fix_cost}. Recommend sources and strategy.",
        [
          {
            field: "recommended_providers",
            as: "recommended_providers",
            type: "string",
          },
          {
            field: "provider_field_mapping",
            as: "provider_field_mapping",
            type: "string",
          },
          {
            field: "estimated_coverage_improvement",
            as: "estimated_improvement",
            type: "string",
          },
          {
            field: "implementation_plan",
            as: "implementation_plan",
            type: "string",
          },
          { field: "budget_estimate", as: "budget_estimate", type: "string" },
          { field: "quick_wins", as: "quick_wins", type: "string" },
        ],
      ),

      // 7. Coverage report delivery Slack + email
      slackNode(
        "7",
        "Deliver Enrichment Audit Report",
        1480,
        "WEEKLY ENRICHMENT AUDIT REPORT\\n\\nCoverage: {overall_fill_rate}% (Grade: {coverage_grade}) | Quality: {quality_score}/100 ({quality_grade})\\nField report: {field_report}\\nMissing critical: {missing_critical}\\nEnrichment: {enrichment_coverage}\\n\\nGAPS: {prioritized_gaps}\\nTop priority: {top_priority_field} (impact: {top_priority_impact})\\n\\nRECOMMENDED: {recommended_providers}\\nMapping: {provider_field_mapping}\\nImprovement: {estimated_improvement}\\nQuick wins: {quick_wins}\\nBudget: {budget_estimate}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1784. EVENT BADGE-SCAN FOLLOW-UP (9 nodes)
  // ===================================================================

  "Event Badge-Scan Follow-up": {
    summary:
      "Fires when badge scan data is imported via webhook, normalizes data with runCode, deduplicates against CRM in HubSpot, enriches the company via Clearbit API, validates email via API, merges conversation notes with ICP scoring via runCode, routes by tier via condition, drafts personalized follow-up with AI, and upserts CRM plus sends email via HubSpot.",
    nodes: [
      trigger("Webhook: Badge scan import", "webhook_app", {
        source: "event_platform",
        event: "badge_scan.batch_imported",
        howItStarts:
          "Fires when badge scan data from a trade show or conference is imported — includes attendee contact information, booth visit notes, conversation summaries, and scan metadata",
        setup:
          "Configure event platform webhook for badge scan imports. Set OpenAI API key for extraction and personalized drafting. Set Clearbit API key for enrichment. Set ZeroBounce API key for email validation. Configure HubSpot token for CRM upsert. Configure email credentials.",
        inputFields: [],
      }),

      // 2. AI extract and normalize
      aiNode(
        "2",
        "AI Extract Badge Scan Data",
        280,
        "You are an event lead data parser. Extract the attendee name, email, phone, company, job title, the booth visit timestamp, the conversation notes or interests captured during the scan, the event name, booth number, the scanner rep name, and any tags or categories assigned during the scan from the raw webhook payload. Respond JSON: {attendee_name: string, attendee_email: string, attendee_phone: string, attendee_company: string, attendee_title: string, scan_timestamp: string, conversation_notes: string, event_name: string, booth_number: string, scanner_rep: string, scan_tags: string, interest_level: string}",
        "Raw webhook payload: {webhook_payload}. Extract all badge scan data regardless of field naming conventions.",
        [
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_phone", as: "attendee_phone", type: "string" },
          { field: "attendee_company", as: "attendee_company", type: "string" },
          { field: "attendee_title", as: "attendee_title", type: "string" },
          { field: "scan_timestamp", as: "scan_timestamp", type: "string" },
          {
            field: "conversation_notes",
            as: "conversation_notes",
            type: "string",
          },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "booth_number", as: "booth_number", type: "string" },
          { field: "scanner_rep", as: "scanner_rep", type: "string" },
          { field: "scan_tags", as: "scan_tags", type: "string" },
          { field: "interest_level", as: "interest_level", type: "string" },
        ],
      ),

      // 3. Normalization runCode
      n("3", "runCode", "Normalize Badge Scan Data", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize and clean badge scan data
const email = (vars.attendee_email || "").trim().toLowerCase();
const name = (vars.attendee_name || "").trim();
const company = (vars.attendee_company || "").trim();
const nameParts = name.split(" ").filter(Boolean);
const firstName = nameParts[0] || "";
const lastName = nameParts.slice(1).join(" ") || "";
const emailDomain = email.split("@")[1] || "";
const isPersonalEmail = ["gmail.com","yahoo.com","hotmail.com","outlook.com"].includes(emailDomain);
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
const isValidFormat = emailRegex.test(email);
return {
  normalized_email: email,
  first_name: firstName,
  last_name: lastName,
  normalized_company: company,
  email_domain: emailDomain,
  is_personal_email: isPersonalEmail ? "true" : "false",
  is_valid_format: isValidFormat ? "true" : "false",
  dedup_key: email
};`,
          outputVariables: [
            { name: "normalized_email", type: "string" },
            { name: "first_name", type: "string" },
            { name: "last_name", type: "string" },
            { name: "normalized_company", type: "string" },
            { name: "email_domain", type: "string" },
            { name: "is_personal_email", type: "string" },
            { name: "is_valid_format", type: "string" },
            { name: "dedup_key", type: "string" },
          ],
        },
      }),

      // 4. CRM dedup check HubSpot
      hubspotGetNode(
        "4",
        "Check CRM for Duplicates",
        760,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "total",
            customName: "existing_contact_count",
            type: "number",
          },
          {
            name: "results[0].id",
            customName: "existing_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "existing_lifecycle",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "existing_owner",
            type: "string",
          },
        ],
      ),

      // 5. Company enrichment Clearbit API
      n("5", "hitApi", "Enrich Company via Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={email_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "enriched_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation API
      n("6", "hitApi", "Validate Attendee Email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={normalized_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. ICP conversation merge runCode
      n("7", "runCode", "Score ICP and Merge Conversation Data", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score ICP fit and combine with conversation notes for tier routing
const employees = parseInt(vars.company_employees) || 0;
const interestLevel = (vars.interest_level || "").toLowerCase();
const notes = vars.conversation_notes || "";
const tags = (vars.scan_tags || "").toLowerCase();
const emailValid = vars.email_status === "valid";
let icpScore = 0;
if (employees >= 100 && employees <= 5000) icpScore += 30;
else if (employees > 5000) icpScore += 20;
else icpScore += 10;
if (interestLevel === "hot" || interestLevel === "high") icpScore += 30;
else if (interestLevel === "warm" || interestLevel === "medium") icpScore += 20;
else icpScore += 10;
if (notes.length > 20) icpScore += 15;
if (tags.includes("demo") || tags.includes("trial")) icpScore += 15;
if (emailValid) icpScore += 10;
const tier = icpScore >= 70 ? "tier_1_hot" : icpScore >= 45 ? "tier_2_warm" : "tier_3_nurture";
const isHighPriority = icpScore >= 60 ? "true" : "false";
return {
  icp_score: Math.min(icpScore, 100),
  lead_tier: tier,
  is_high_priority: isHighPriority,
  merged_context: "Interest: " + interestLevel + " | Notes: " + (notes.substring(0, 100) || "none") + " | Tags: " + (tags || "none"),
  follow_up_urgency: tier === "tier_1_hot" ? "24_hours" : tier === "tier_2_warm" ? "48_hours" : "1_week"
};`,
          outputVariables: [
            { name: "icp_score", type: "number" },
            { name: "lead_tier", type: "string" },
            { name: "is_high_priority", type: "string" },
            { name: "merged_context", type: "string" },
            { name: "follow_up_urgency", type: "string" },
          ],
        },
      }),

      // 8. Tier routing condition
      n("8", "condition", "Is High-Priority Tier 1?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_priority", operator: "eq", value: "true" },
          ],
        },
      }),

      // 9. AI personalized draft + CRM upsert + send
      aiNode(
        "9",
        "AI Draft Personalized Follow-Up",
        1960,
        "You are a trade show follow-up expert. Given an attendee who visited your booth, their conversation notes, interest level, company context, and ICP score, craft a warm, personalized follow-up email that references the specific conversation and provides relevant next steps. Respond JSON: {follow_up_subject: string, follow_up_body_html: string, recommended_cta: string, personalization_note: string}",
        "Attendee: {first_name} {last_name} ({attendee_title}) at {enriched_company} ({company_employees} employees, {company_industry}). Event: {event_name}. Rep: {scanner_rep}. Interest: {interest_level}. Notes: {conversation_notes}. Tags: {scan_tags}. Tier: {lead_tier} ({icp_score}/100). Follow-up urgency: {follow_up_urgency}. Craft the follow-up.",
        [
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          {
            field: "follow_up_body_html",
            as: "follow_up_body_html",
            type: "string",
          },
          { field: "recommended_cta", as: "recommended_cta", type: "string" },
          {
            field: "personalization_note",
            as: "personalization_note",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1785. EVENT SIGNUP SCORE ROUTE (7 nodes)
  // ===================================================================

  "Event Signup Score Route": {
    summary:
      "Fires when a registration event arrives via webhook, extracts registrant data with AI, enriches via Clearbit API, scores ICP fit with runCode, scores engagement intent with runCode, routes via composite gate condition, sends pre-event AE outreach email, and creates a CRM record plus tag in HubSpot.",
    nodes: [
      trigger("Webhook: Event registration received", "webhook_app", {
        source: "event_platform",
        event: "registration.completed",
        howItStarts:
          "Fires when a prospect registers for a webinar, conference, workshop, or product demo event — including all registration details, UTM parameters, and registration source",
        setup:
          "Configure event platform webhook for registration events. Set OpenAI API key for extraction. Set Clearbit API key for enrichment. Configure email credentials for pre-event outreach. Set HubSpot token for CRM creation.",
        inputFields: [],
      }),

      // 2. AI extract registrant data
      aiNode(
        "2",
        "AI Extract Registration Data",
        280,
        "You are an event registration data parser. Extract the registrant name, email, company, job title, phone, the event name, event date, event type (webinar, conference, workshop, demo), registration source, UTM parameters, and any custom questions answered during registration from the raw webhook payload. Respond JSON: {registrant_name: string, registrant_email: string, registrant_company: string, registrant_title: string, registrant_phone: string, event_name: string, event_date: string, event_type: string, registration_source: string, utm_source: string, utm_campaign: string, custom_answers: string}",
        "Raw webhook payload: {webhook_payload}. Extract all event registration data regardless of field naming conventions.",
        [
          { field: "registrant_name", as: "registrant_name", type: "string" },
          { field: "registrant_email", as: "registrant_email", type: "string" },
          {
            field: "registrant_company",
            as: "registrant_company",
            type: "string",
          },
          { field: "registrant_title", as: "registrant_title", type: "string" },
          { field: "registrant_phone", as: "registrant_phone", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          {
            field: "registration_source",
            as: "registration_source",
            type: "string",
          },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "custom_answers", as: "custom_answers", type: "string" },
        ],
      ),

      // 3. Registrant enrichment Clearbit API
      n("3", "hitApi", "Enrich Registrant via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={registrant_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "enriched_employees",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "company.metrics.estimatedAnnualRevenue",
              customName: "enriched_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 4. ICP fit scoring runCode
      n("4", "runCode", "Score ICP Fit", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score ICP fit based on enrichment
const employees = parseInt(vars.enriched_employees) || 0;
const seniority = (vars.enriched_seniority || "").toLowerCase();
const industry = (vars.enriched_industry || "").toLowerCase();
const title = (vars.enriched_title || "").toLowerCase();
let icpScore = 0;
if (employees >= 100 && employees <= 5000) icpScore += 30;
else if (employees > 5000) icpScore += 20;
else if (employees >= 25) icpScore += 15;
else icpScore += 5;
if (seniority === "executive" || seniority === "director") icpScore += 25;
else if (seniority === "manager") icpScore += 15;
else icpScore += 5;
const targetTitles = ["marketing", "sales", "revenue", "growth", "demand gen"];
if (targetTitles.some(t => title.includes(t))) icpScore += 20;
else icpScore += 5;
const targetIndustries = ["technology", "software", "saas", "internet", "financial services"];
if (targetIndustries.some(i => industry.includes(i))) icpScore += 15;
else icpScore += 5;
return {
  icp_fit_score: Math.min(icpScore, 100),
  icp_tier: icpScore >= 65 ? "high_fit" : icpScore >= 40 ? "medium_fit" : "low_fit",
  fit_factors: "emp:" + employees + " sen:" + seniority + " title:" + title.substring(0, 20) + " ind:" + industry.substring(0, 20)
};`,
          outputVariables: [
            { name: "icp_fit_score", type: "number" },
            { name: "icp_tier", type: "string" },
            { name: "fit_factors", type: "string" },
          ],
        },
      }),

      // 5. Engagement-intent scoring runCode
      n("5", "runCode", "Score Engagement Intent", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score engagement intent based on registration context
const eventType = (vars.event_type || "").toLowerCase();
const source = (vars.registration_source || "").toLowerCase();
const utmSource = (vars.utm_source || "").toLowerCase();
const customAnswers = (vars.custom_answers || "").toLowerCase();
const icpFit = parseInt(vars.icp_fit_score) || 0;
let intentScore = 0;
if (eventType.includes("demo")) intentScore += 30;
else if (eventType.includes("workshop")) intentScore += 25;
else if (eventType.includes("webinar")) intentScore += 15;
else intentScore += 10;
if (source.includes("direct") || source.includes("referral")) intentScore += 20;
else if (utmSource.includes("linkedin") || utmSource.includes("google")) intentScore += 15;
else intentScore += 5;
if (customAnswers.includes("urgent") || customAnswers.includes("evaluating") || customAnswers.includes("buying")) intentScore += 20;
else if (customAnswers.includes("interested") || customAnswers.includes("learning")) intentScore += 10;
const compositeScore = Math.round((icpFit * 0.5) + (intentScore * 0.5));
const isHighComposite = compositeScore >= 55 ? "true" : "false";
const outreachTiming = compositeScore >= 70 ? "immediate" : compositeScore >= 45 ? "pre_event" : "post_event";
return {
  intent_score: Math.min(intentScore, 100),
  composite_score: compositeScore,
  is_high_composite: isHighComposite,
  outreach_timing: outreachTiming,
  intent_signals: "event:" + eventType + " source:" + source + " answers:" + (customAnswers.substring(0, 30) || "none")
};`,
          outputVariables: [
            { name: "intent_score", type: "number" },
            { name: "composite_score", type: "number" },
            { name: "is_high_composite", type: "string" },
            { name: "outreach_timing", type: "string" },
            { name: "intent_signals", type: "string" },
          ],
        },
      }),

      // 6. Composite route gate condition
      n("6", "condition", "Is High-Composite Score?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_composite", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Pre-event AE outreach email + CRM record HubSpot
      emailNode(
        "7",
        "Send Pre-Event AE Outreach Email",
        1480,
        "{registrant_email}",
        "Looking forward to {event_name}, {registrant_name}!",
        "<p>Hi {registrant_name},</p><p>Thank you for registering for <strong>{event_name}</strong> on {event_date}! We noticed you are from {enriched_company} and wanted to personally reach out.</p><p>As someone in {enriched_title} at a {enriched_industry} company, you will find the session especially relevant. We have helped similar companies in your space and would love to connect before or during the event.</p><p>Would you be open to a quick 15-minute chat before the event to make sure we cover the topics most relevant to your team?</p><p>Looking forward to meeting you!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1786. EXECUTIVE BRIEFING DOCUMENT (8 nodes)
  // ===================================================================

  "Executive Briefing Document": {
    summary:
      "Fires when a meeting prep request arrives via webhook, extracts meeting data with AI, pulls deal and account intelligence from HubSpot, researches executive profiles with AI, performs strategic analysis with AI, maps alignment with AI, preempts objections with AI, assembles a one-page briefing with runCode, and delivers via email and Slack.",
    nodes: [
      trigger("Webhook: Executive meeting prep requested", "webhook_app", {
        source: "calendar",
        event: "meeting.executive_prep_requested",
        howItStarts:
          "Fires when an AE or sales leader requests an executive briefing document for an upcoming C-level meeting — typically for deals in late stage with senior stakeholders",
        setup:
          "Configure calendar/CRM webhook for executive meeting prep requests. Set OpenAI API key for all AI research and analysis nodes. Configure HubSpot token for deal intelligence. Configure Slack webhook for briefing delivery. Set email credentials.",
        inputFields: [],
      }),

      // 2. AI extract meeting prep data
      aiNode(
        "2",
        "AI Extract Meeting Prep Data",
        280,
        "You are a sales operations data parser. Extract the executive name, executive title, executive email, their company name, the deal name, deal ID, the meeting date and time, the requesting AE name, meeting objective, any specific topics to cover, and the executive LinkedIn profile URL from the raw webhook payload. Respond JSON: {exec_name: string, exec_title: string, exec_email: string, exec_company: string, deal_name: string, deal_id: string, meeting_datetime: string, requesting_ae: string, meeting_objective: string, topics_requested: string, exec_linkedin: string, meeting_duration: string}",
        "Raw webhook payload: {webhook_payload}. Extract all executive meeting prep data regardless of field naming conventions.",
        [
          { field: "exec_name", as: "exec_name", type: "string" },
          { field: "exec_title", as: "exec_title", type: "string" },
          { field: "exec_email", as: "exec_email", type: "string" },
          { field: "exec_company", as: "exec_company", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "meeting_datetime", as: "meeting_datetime", type: "string" },
          { field: "requesting_ae", as: "requesting_ae", type: "string" },
          {
            field: "meeting_objective",
            as: "meeting_objective",
            type: "string",
          },
          { field: "topics_requested", as: "topics_requested", type: "string" },
          { field: "exec_linkedin", as: "exec_linkedin", type: "string" },
          { field: "meeting_duration", as: "meeting_duration", type: "string" },
        ],
      ),

      // 3. Deal account intelligence pull HubSpot
      hubspotGetNode(
        "3",
        "Pull Deal and Account Intelligence",
        520,
        "crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,pipeline,closedate,hs_deal_stage_probability,notes_last_contacted,num_associated_contacts,hs_next_step",
        [
          {
            name: "properties.dealname",
            customName: "hs_deal_name",
            type: "string",
          },
          {
            name: "properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "properties.pipeline",
            customName: "deal_pipeline",
            type: "string",
          },
          {
            name: "properties.closedate",
            customName: "expected_close",
            type: "string",
          },
          {
            name: "properties.hs_deal_stage_probability",
            customName: "win_probability",
            type: "string",
          },
          {
            name: "properties.hs_next_step",
            customName: "current_next_step",
            type: "string",
          },
          {
            name: "properties.num_associated_contacts",
            customName: "stakeholder_count",
            type: "string",
          },
        ],
      ),

      // 4. AI executive profile research
      aiNode(
        "4",
        "AI Research Executive Profile",
        760,
        "You are an executive intelligence researcher. Given an executive name, title, company, and LinkedIn URL, research their professional background, career trajectory, known priorities, public statements or thought leadership, communication style preferences, and potential hot-button topics. Respond JSON: {career_summary: string, known_priorities: string, public_statements: string, communication_style: string, hot_button_topics: string, mutual_connections_approach: string, personal_interests: string}",
        "Executive: {exec_name}, Title: {exec_title}, Company: {exec_company}. LinkedIn: {exec_linkedin}. Meeting objective: {meeting_objective}. Topics: {topics_requested}. Research this executive thoroughly.",
        [
          { field: "career_summary", as: "career_summary", type: "string" },
          { field: "known_priorities", as: "known_priorities", type: "string" },
          {
            field: "public_statements",
            as: "public_statements",
            type: "string",
          },
          {
            field: "communication_style",
            as: "communication_style",
            type: "string",
          },
          {
            field: "hot_button_topics",
            as: "hot_button_topics",
            type: "string",
          },
          {
            field: "mutual_connections_approach",
            as: "mutual_connections",
            type: "string",
          },
          {
            field: "personal_interests",
            as: "personal_interests",
            type: "string",
          },
        ],
      ),

      // 5. AI strategic analysis
      aiNode(
        "5",
        "AI Strategic Analysis",
        1000,
        "You are a strategic sales advisor. Given the deal context (amount, stage, probability, next step) and executive profile (priorities, communication style, hot buttons), perform a strategic analysis that identifies the best angle of approach, value framing, and ROI narrative for this specific executive. Respond JSON: {strategic_angle: string, value_framing: string, roi_narrative: string, risk_factors: string, success_metrics_to_highlight: string, competitive_positioning: string}",
        "Deal: {hs_deal_name}, Amount: ${deal_amount}, Stage: {deal_stage}, Win probability: {win_probability}%, Close: {expected_close}, Stakeholders: {stakeholder_count}. Executive priorities: {known_priorities}. Style: {communication_style}. Hot buttons: {hot_button_topics}. Objective: {meeting_objective}. Perform strategic analysis.",
        [
          { field: "strategic_angle", as: "strategic_angle", type: "string" },
          { field: "value_framing", as: "value_framing", type: "string" },
          { field: "roi_narrative", as: "roi_narrative", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "success_metrics_to_highlight",
            as: "success_metrics",
            type: "string",
          },
          {
            field: "competitive_positioning",
            as: "competitive_positioning",
            type: "string",
          },
        ],
      ),

      // 6. AI objection preemption
      aiNode(
        "6",
        "AI Preempt Likely Objections",
        1240,
        "You are a sales objection handling expert. Given an executive profile, deal context, risk factors, and competitive positioning, anticipate the top 3-5 objections this executive is likely to raise and prepare concise, confident responses. Respond JSON: {objection_1: string, response_1: string, objection_2: string, response_2: string, objection_3: string, response_3: string, general_deflection: string, confidence_builder: string}",
        "Executive: {exec_name} ({exec_title}). Priorities: {known_priorities}. Hot buttons: {hot_button_topics}. Deal: ${deal_amount} at {deal_stage}. Risks: {risk_factors}. Competitive: {competitive_positioning}. Style: {communication_style}. Anticipate and prepare objection responses.",
        [
          { field: "objection_1", as: "objection_1", type: "string" },
          { field: "response_1", as: "response_1", type: "string" },
          { field: "objection_2", as: "objection_2", type: "string" },
          { field: "response_2", as: "response_2", type: "string" },
          { field: "objection_3", as: "objection_3", type: "string" },
          { field: "response_3", as: "response_3", type: "string" },
          {
            field: "general_deflection",
            as: "general_deflection",
            type: "string",
          },
          {
            field: "confidence_builder",
            as: "confidence_builder",
            type: "string",
          },
        ],
      ),

      // 7. One-page briefing assembly runCode
      n("7", "runCode", "Assemble One-Page Briefing", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble all research into a structured one-page briefing
const briefing = [
  "EXECUTIVE BRIEFING: " + (vars.exec_name || "") + " | " + (vars.exec_company || ""),
  "Meeting: " + (vars.meeting_datetime || "") + " (" + (vars.meeting_duration || "30") + " min)",
  "Deal: " + (vars.hs_deal_name || "") + " | $" + (vars.deal_amount || "0") + " | " + (vars.deal_stage || ""),
  "",
  "EXECUTIVE PROFILE:",
  "Career: " + (vars.career_summary || "N/A"),
  "Priorities: " + (vars.known_priorities || "N/A"),
  "Style: " + (vars.communication_style || "N/A"),
  "",
  "STRATEGIC APPROACH:",
  "Angle: " + (vars.strategic_angle || "N/A"),
  "Value Framing: " + (vars.value_framing || "N/A"),
  "ROI Narrative: " + (vars.roi_narrative || "N/A"),
  "",
  "OBJECTION PREP:",
  "1. " + (vars.objection_1 || "") + " -> " + (vars.response_1 || ""),
  "2. " + (vars.objection_2 || "") + " -> " + (vars.response_2 || ""),
  "3. " + (vars.objection_3 || "") + " -> " + (vars.response_3 || ""),
  "",
  "KEY METRICS: " + (vars.success_metrics || "N/A"),
  "COMPETITIVE: " + (vars.competitive_positioning || "N/A"),
  "RISKS: " + (vars.risk_factors || "N/A")
].join("\\n");
const wordCount = briefing.split(/\\s+/).length;
return {
  briefing_document: briefing,
  briefing_word_count: wordCount,
  briefing_title: "Exec Brief: " + (vars.exec_name || "") + " @ " + (vars.exec_company || ""),
  is_complete: wordCount > 100 ? "true" : "false"
};`,
          outputVariables: [
            { name: "briefing_document", type: "string" },
            { name: "briefing_word_count", type: "number" },
            { name: "briefing_title", type: "string" },
            { name: "is_complete", type: "string" },
          ],
        },
      }),

      // 8. Delivery email + Slack
      slackNode(
        "8",
        "Deliver Executive Briefing",
        1720,
        "{briefing_title}\\n\\n{briefing_document}\\n\\nConfidence builder: {confidence_builder}\\nGeneral deflection: {general_deflection}\\n\\nPrepared for: {requesting_ae} | Word count: {briefing_word_count}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1787. EXPORT TO SEQUENCER (7 nodes)
  // ===================================================================

  "Export to Sequencer": {
    summary:
      "Fires when a lead is qualified via webhook, validates qualification with runCode, cross-checks suppression lists in HubSpot, drafts an opening message with AI, transforms data for sequencer format with runCode, imports to the sequencer via API, and syncs CRM status plus export summary in HubSpot and Sheets.",
    nodes: [
      trigger("Webhook: Lead qualified for sequencing", "webhook_app", {
        source: "crm",
        event: "lead.qualified_for_sequence",
        howItStarts:
          "Fires when a lead reaches qualified status and is ready to be exported to an outbound sequencer (Outreach, SalesLoft, Apollo) — includes all enriched lead data and qualification context",
        setup:
          "Configure CRM webhook for lead qualification events. Set OpenAI API key for opening draft. Configure sequencer API credentials (Outreach/SalesLoft). Set HubSpot token for suppression check and status sync. Set Google Sheets credentials for export logging.",
        inputFields: [],
      }),

      // 2. AI extract lead qualification data
      aiNode(
        "2",
        "AI Extract Qualified Lead Data",
        280,
        "You are a lead qualification data parser. Extract the lead name, email, company, job title, phone, the qualification reason, lead score, qualifying rep name, the target sequence name, any personalization notes, the lead source, and the company industry and size from the raw webhook payload. Respond JSON: {lead_name: string, lead_email: string, lead_company: string, lead_title: string, lead_phone: string, qualification_reason: string, lead_score: string, qualifying_rep: string, target_sequence: string, personalization_notes: string, lead_source: string, company_industry: string, company_size: string}",
        "Raw webhook payload: {webhook_payload}. Extract all qualified lead data regardless of field naming conventions.",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          {
            field: "qualification_reason",
            as: "qualification_reason",
            type: "string",
          },
          { field: "lead_score", as: "lead_score", type: "string" },
          { field: "qualifying_rep", as: "qualifying_rep", type: "string" },
          { field: "target_sequence", as: "target_sequence", type: "string" },
          {
            field: "personalization_notes",
            as: "personalization_notes",
            type: "string",
          },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "company_industry", as: "company_industry", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
        ],
      ),

      // 3. Lead qualification check runCode
      n("3", "runCode", "Validate Lead Qualification", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate the lead meets minimum requirements for sequencing
const email = (vars.lead_email || "").trim().toLowerCase();
const name = (vars.lead_name || "").trim();
const company = (vars.lead_company || "").trim();
const score = parseInt(vars.lead_score) || 0;
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
const isValidEmail = emailRegex.test(email);
const isGenericEmail = ["info@","admin@","support@","noreply@","sales@","contact@"].some(p => email.startsWith(p));
const isFreeDomain = ["gmail.com","yahoo.com","hotmail.com","outlook.com","aol.com"].some(d => email.endsWith(d));
const errors = [];
if (!isValidEmail) errors.push("invalid_email_format");
if (isGenericEmail) errors.push("generic_email");
if (!name) errors.push("missing_name");
if (!company) errors.push("missing_company");
if (score < 30) errors.push("low_score");
const isQualified = errors.length === 0;
const sequenceReady = isQualified && !isFreeDomain;
return {
  validated_email: email,
  is_qualified: isQualified ? "true" : "false",
  is_sequence_ready: sequenceReady ? "true" : "false",
  validation_errors: errors.join(", ") || "none",
  is_free_domain: isFreeDomain ? "true" : "false",
  quality_flag: errors.length === 0 ? "clean" : errors.length <= 1 ? "minor_issues" : "needs_review"
};`,
          outputVariables: [
            { name: "validated_email", type: "string" },
            { name: "is_qualified", type: "string" },
            { name: "is_sequence_ready", type: "string" },
            { name: "validation_errors", type: "string" },
            { name: "is_free_domain", type: "string" },
            { name: "quality_flag", type: "string" },
          ],
        },
      }),

      // 4. Suppression cross-check HubSpot
      hubspotGetNode(
        "4",
        "Check Suppression List in HubSpot",
        760,
        "crm/v3/objects/contacts/search",
        [
          { name: "total", customName: "existing_records", type: "number" },
          {
            name: "results[0].properties.hs_email_optout",
            customName: "is_opted_out",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "current_status",
            type: "string",
          },
          {
            name: "results[0].properties.hs_sequences_is_enrolled",
            customName: "already_in_sequence",
            type: "string",
          },
        ],
      ),

      // 5. AI opening draft
      aiNode(
        "5",
        "AI Draft Sequence Opening",
        1000,
        "You are a world-class B2B cold outreach copywriter. Given a qualified lead profile including their title, company, industry, lead source, and personalization notes, craft a compelling first-touch email for an outbound sequence. Be concise (under 100 words), personalized, and include a clear CTA. Respond JSON: {opening_subject: string, opening_body: string, personalization_hook: string, call_to_action: string, follow_up_angle: string}",
        "Lead: {lead_name}, Title: {lead_title}, Company: {lead_company} ({company_size} employees, {company_industry}). Source: {lead_source}. Score: {lead_score}. Qualification: {qualification_reason}. Notes: {personalization_notes}. Sequence: {target_sequence}. Rep: {qualifying_rep}. Craft the opening.",
        [
          { field: "opening_subject", as: "opening_subject", type: "string" },
          { field: "opening_body", as: "opening_body", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          { field: "call_to_action", as: "call_to_action", type: "string" },
          { field: "follow_up_angle", as: "follow_up_angle", type: "string" },
        ],
      ),

      // 6. Sequencer transformation + import API
      n("6", "hitApi", "Import Lead to Sequencer via API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.outreach.io/api/v2/prospects",
          headers: [
            { key: "Authorization", value: "Bearer {outreach_api_key}" },
            { key: "Content-Type", value: "application/vnd.api+json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"type":"prospect","attributes":{"emails":["{validated_email}"],"firstName":"{lead_name}","lastName":"","title":"{lead_title}","company":"{lead_company}","phones":["{lead_phone}"],"custom1":"{qualification_reason}","custom2":"{personalization_hook}","custom3":"{lead_source}","tags":["{target_sequence}","{quality_flag}"]}}',
            },
          ],
          variables: [
            {
              name: "data.id",
              customName: "sequencer_prospect_id",
              type: "string",
            },
            {
              name: "data.attributes.emails[0]",
              customName: "sequencer_email",
              type: "string",
            },
            {
              name: "data.attributes.createdAt",
              customName: "sequencer_created_at",
              type: "string",
            },
          ],
        },
      }),

      // 7. CRM status sync + export summary HubSpot + Sheets
      sheetsNode(
        "7",
        "Log Export Summary to Sheets + CRM Sync",
        1480,
        "SequencerExport",
        '[["{lead_name}","{validated_email}","{lead_company}","{lead_title}","{target_sequence}","{lead_score}","{qualification_reason}","{sequencer_prospect_id}","{quality_flag}","{opening_subject}","{qualifying_rep}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
